// High Level Finance — On-Track Technologies custom (/customs/high-level-finance).
//
// The legacy "High level Finance Tracking" dashboard rebuilt as a custom
// page in the Work Allocations / Finance-custom design language (sticky collapsible Filters
// card, clickable stat tiles, hand-rolled SVG line charts, sortable tables
// with per-table Excel export), FULL WIDTH, with deliberately FEW filters:
// search · Project (multi-select) · Project Owner (multi-select; the
// "Project" work item's Assigned resource) · Status (a multi-select of
// every status on the dashboard; PMO and Adjust Time deselected by
// default) · due-date range (This Year default) · Hide Done Projects.
// Every widget shows the SAME filtered rows. The two charts share ONE
// series selection (a full-width bar under them, with a rotary KNOB —
// left = Theoretical Projections, top = CUSTOM, right = Actuals); each
// chart's own legend lists the visible series. Default: the knob at
// CUSTOM showing every line plus Actual Profit; Projected Profit off.
// Under every series sits a joystick: its TREND LINE (least-squares fit
// to the last complete period, dotted projection after, 95% band, ×
// on excluded outliers — see ./trend.js) on both charts; at most TWO at a
// time, all off by default. ONE control row (Fit to · Remove Outliers % ·
// Optimize for Best Reliability) applies to every trend line that is on.
//
// Widgets, top to bottom (the two charts sit side by side, as do the two
// invoice matrices, and the tiles are ONE row — at every width; only a
// phone (≤640px) stacks them):
//   1. Stat tiles — Projected Income · Debtors · Projected Expense ·
//      Expenses · Salaries & Special Projects (click one to isolate it),
//      one row across the full width.
//   2. Expenses & Incomes — per period (5 series, as the legacy chart:
//      Projected Expense, Total Expenses INCL Salaries & Overtime, Debtors,
//      Projected Income, Salaries & Overtime Only) + TODAY marker, with
//      "Projected Profit" and "Actual Profit" COLUMN overlays on their own
//      right-hand axis: green columns up for a positive period, red
//      columns down for a negative one (ⓘ in the legend = the formula).
//   3. The same, cumulative (to the right of 2).
//   4. Projects Finance Summary (beside 5) — per project: Projected Income / Debtors /
//      Projected Expense / Expenses / Salaries & Special Projects /
//      Projected Profit / Actual Profit.
//   5. Debtors per Status — projects × the Debtors workflow's statuses.
//   6. Invoices NOT PAID — Debtors not yet paid, projects × due month.
//   7. Invoices NOT INVOICED — Debtors not yet invoiced, projects × due
//      month (to the right of 6).
//   8. Expenses per Supplier — Procurement Orders by Supplier List: a line
//      per supplier over time + the ranked supplier table.
//   (The Summary of Invoiceable Work section was retired on 2026-09-21.)
//
// Field wiring is DATA, not code: every money number is the ONE field
// "ZAR Value (Excl VAT)" (customfield_10071 live), resolved AT RUNTIME by
// field NAME against /api/fields (names normalized: case/punctuation
// insensitive); the COLUMN a rand lands in is decided by WORK TYPE:
//   Projected Income  = Σ "Projected Income"        Debtors  = Σ "Debtors"
//   Projected Expense = Σ "Projected Expense Budget" Expenses = Σ "Procurement Order" + "Outsourcing"
//   Salaries & Special Projects = Σ "Salary Expense" + "Special Projects"
//   Total Expenses (chart)      = Expenses + Salaries & Special Projects
//   Projected Profit            = Projected Income − Projected Expense
//   Actual Profit               = Debtors − (Expenses + Salaries & Special Projects)
// Supplier = "Supplier List" (10065), Client = "Client Name" (10110),
// hours = "Actual Time (Hours)" (10037), all by name. The page row's
// config may override everything:
//   { "valueField": "customfield_xxxxx" | "ZAR Value (Excl VAT)",
//     "supplierField": "...", "clientField": "...", "hoursField": "...",
//     "projectedIncomeTypes": ["Projected Income"],
//     "debtorTypes": ["Debtors"],
//     "projectedExpenseTypes": ["Projected Expense Budget"],
//     "expenseTypes": ["Procurement Order", "Outsourcing"],
//     "salaryTypes": ["Salary Expense", "Special Projects"],
//     "projectTypes": ["Project"],           // carries due date / Done flag
//     "excludedStatuses": ["ARCHIVE"],       // never shown
//     "paidStatuses": ["PAID", "TAX INVOICED"],
//     "invoicedStatuses": ["INVOICED"],
//     "hiddenStatuses": ["PMO", "Adjust Time"],  // deselected in the Status filter by default
//     "debtorStatusOrder": ["To Do", "PMO", "PF INVOICE", "INVOICED", "PAID", "TAX INVOICED"],
//     "defaults": { "datePreset": "year" | "all", "trendFitTo": "last" | "this",   // what the page opens with
//                   "seriesOff": ["prof"] },                           // series unticked on load
//     "filters":  { "search": true, "project": true, "owner": true, "status": true,
//                   "dateRange": true } }                              // which controls show
//     "baseZql": "…",                        // background scope AND-ed with every load
//     "filterButtons": [{ "id": "…", "caption": "CNC", "zql": "…", "on": false }] }  // quick buttons
//   An admin edits all of this on the page itself in admin mode
//   (Ctrl+Space): a ⚙ on the Filters card (scope, defaults, quick buttons,
//   which controls show) and a ⚙ on the Projects Finance Summary (the money
//   field, the work types per column, the status lists) — the same
//   AdminSettings box the Operations custom uses, with Test counts per ZQL
//   and one Save (PUT /api/customs/:slug, live without a reload).
//
// Every number is a door (the Tempo custom-chart convention): ONE line point
// or ONE column on a chart opens only that series' items for that period;
// the period's x-axis label opens the whole interval; a table cell, a row
// total, a supplier or an hours cell opens what is behind it — all in
// Zebra Search (/issues?jql=key in (…)).
//   (the Debtors per Status columns: lifecycle order first, any other
//   workflow status after it — /api/issue-type-statuses lists alphabetically)
//
// Loads: `type in (<finance types>) order by duedate asc`, paged — only the
// finance-bearing types, never the whole site — and, after it, the hours
// load `<hoursField> > 0 order by duedate asc`. The Project filter works
// client-side on the item key prefix (ZQL's `project in (...)` resolves
// via space_id, which cloned items don't carry — a sister-fork learning).
//
// DEV ONLY: `?demo=1` on the vite dev server renders fixture data
// (./demoData.js) so the page can be reviewed before items are cloned.
//
// Props: { page, api }.  api is the full app api (@app/services/api).

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTab } from '@platform/app-shell/frontend/contexts/TabContext';
import LineChart from './LineChart';
import InfoIcon from './InfoIcon';
import KnobToggle from './KnobToggle';
import TrendToggle from './TrendToggle';
import AdminSettings from './AdminSettings';
import { withStandardButtons } from '../standardButtons';
import LoadingCard from '../LoadingCard';
import { MONEY_KINDS } from './kinds';
import {
  DARK, FONT, LIGHT, NONE, PALETTE, assigneeOf, bucketLabel, bucketStartOf, dueOf, fmtDue, fmtH, fmtMonth, fmtR, monthsBetween, nextSort,
  normName, numOf, optOf, projOf, round1, round2, statusCat, statusName, typeOf, writeSheet, yearFromISO, yearToISO,
} from './util';

const PAGE = 2000;               // rows per search page
const MAX_ROWS = 20000;          // hard cap per data load
const MAX_KEYS = 250000;         // cap for keys-only loads (the quick buttons' key sets) — a hit is REPORTED, never silent

// Runtime field resolution — the names looked up in /api/fields.
const NAME_VALUE = 'ZAR Value (Excl VAT)';
const NAME_SUPPLIER = 'Supplier List';
const NAME_CLIENT = 'Client Name';
const NAME_HOURS = 'Actual Time (Hours)';

// The money kinds — one list shared with High Level Operations (./kinds.js).
const KINDS = MONEY_KINDS;
const DEFAULT_PROJECT_TYPES = ['Project'];
const DEFAULT_EXCLUDED_STATUSES = ['ARCHIVE'];
const DEFAULT_PAID_STATUSES = ['PAID', 'TAX INVOICED'];
const DEFAULT_INVOICED_STATUSES = ['INVOICED'];
const DEFAULT_STATUS_ORDER = ['To Do', 'PMO', 'PF INVOICE', 'INVOICED', 'PAID', 'TAX INVOICED'];
const DEFAULT_HIDDEN_STATUSES = ['PMO', 'Adjust Time'];   // deselected in the Status filter by default

// How the two profit figures are calculated — the ⓘ hover text.
const INFO_PROJECTED = 'Projected Profit = Projected Income − Projected Expense (Σ ZAR Value (Excl VAT) of the "Projected Income" items minus Σ of the "Projected Expense Budget" items, in the current filters).';
const INFO_TREND = [
  'Each trend line is a least-squares straight line fitted to the series\u2019 period values, from its first non-zero period up to the last COMPLETE period. It is drawn solid across the fitted periods and continued as a dotted straight-line projection with the same slope.',
  'Why the current month is left out (\u201cFit to Last Month\u201d, the default): the month in progress is systematically under-reported \u2014 invoices, orders and salaries for it are still being captured \u2014 and, sitting at the very end of the range, it has the most leverage on the slope. A half-finished month would drag every trend down and bend the projection with it. \u201cThis Month\u201d opts it in when you know the month is fully captured.',
  'The shaded band is the 95% confidence range around the trend: it hugs the line where the periods cluster and fans out as the estimate extrapolates past today.',
  'Reliability is Classical Test Theory\u2019s R = 1 \u2212 \u03c3\u00b2\u2091/\u03c3\u00b2\u2093 \u2014 the share of the series\u2019 variance explained by the trend rather than by noise. \u201cRemove Outliers %\u201d drops that share of the fitted periods with the largest residuals (each is marked \u00d7 on the chart) and refits; excluding outliers shrinks the error variance so reliability rises, but past a point the few remaining periods make the band balloon (the small-sample penalty). The outlier % is set automatically to the value where the 95% band is narrowest on average \u2014 the most certainty gained for the least data removed; step it by hand to override, click the green box to return.',
  'One setting applies to every trend line that is on. The reliability shown in the control row is the per-period chart\u2019s; the cumulative chart fits the running totals and shows its own.',
].join('\n\n');
const CTT_LINK = { href: 'https://ncme.org/wp-content/uploads/2025/10/Didactic_Companion_Article__Lewis__2018_.pdf', label: 'About the method (NCME)' };
const INFO_ACTUAL = 'Actual Profit = Debtors − (Expenses + Salaries & Special Projects) (Σ ZAR Value (Excl VAT) of the "Debtors" items minus Σ of the "Procurement Order", "Outsourcing", "Salary Expense" and "Special Projects" items, in the current filters).';

const SHOW_TOP = 5;              // project tables show the top rows, then "Show more…"
const SUPPLIER_LINES = 12;       // suppliers charted (the rest are in the table)

const lower = (arr) => arr.map((s) => String(s).toLowerCase());
const dash = (ui) => <span style={{ opacity: 0.4, color: ui.muted }}>–</span>;

export default function HighLevelFinance({ page, api }) {
  const { adminMode, openTab } = useTab();
  const [items, setItems] = useState(null);            // finance rows (null = loading)
  const [hoursItems, setHoursItems] = useState(null);  // invoiceable-work rows
  const [error, setError] = useState(null);
  const [hoursError, setHoursError] = useState(null);
  const [fields, setFields] = useState(null);          // /api/fields — for name → id resolution
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState('');
  const [projectSel, setProjectSel] = useState(() => new Set());   // Project filter (multi)
  const [viewKind, setViewKind] = useState('');        // '' = all kinds, else a KINDS id
  // The page config, live: an admin's Save updates it in place (PUT), no reload.
  const [pageCfg, setPageCfg] = useState(() => page?.config || {});
  // What the page opens with — the page config's `defaults`, else the built-ins.
  const D = page?.config?.defaults || {};
  const [dateFrom, setDateFrom] = useState(() => (D.datePreset === 'all' ? '' : yearFromISO()));
  const [dateTo, setDateTo] = useState(() => (D.datePreset === 'all' ? '' : yearToISO()));
  const [datePreset, setDatePreset] = useState(() => (D.datePreset === 'all' ? 'all' : 'year'));   // 'year' | 'all' | 'custom'
  // No "hide done projects" and no "hide undated" switches: a Done project
  // still carries this year's invoices, and an item with no due date cannot
  // sit inside a selected due-date range — it shows only under "All dates".
  // Status filter: the set of statuses DESELECTED (lower-case); PMO and
  // Adjust Time start deselected (page config `hiddenStatuses`).
  const [statusOff, setStatusOff] = useState(() => new Set(lower(page?.config?.hiddenStatuses || DEFAULT_HIDDEN_STATUSES)));
  const [ownerSel, setOwnerSel] = useState(() => new Set());       // Project Owner (multi)
  const [supplierSel, setSupplierSel] = useState(() => new Set()); // suppliers charted in Expenses per Supplier (multi; empty = the top ones)
  const [seriesOff, setSeriesOff] = useState(() => new Set(Array.isArray(D.seriesOff) ? D.seriesOff : ['prof']));   // chart series hidden — shared by both charts; Projected Profit off by default
  const flipIn = (setter) => (k) => setter((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleProject = flipIn(setProjectSel);
  const toggleOwner = flipIn(setOwnerSel);
  const toggleSupplier = flipIn(setSupplierSel);
  const toggleSeries = flipIn(setSeriesOff);
  // Trend lines: which series (max 2), fitted to the last complete or the
  // current period, with an outlier % — shared by every trend on both charts.
  const [trendKeys, setTrendKeys] = useState(() => new Set());
  const [trendFitTo, setTrendFitTo] = useState(() => (D.trendFitTo === 'this' ? 'this' : 'last'));
  // Which filter controls the page shows (page config `filters`; all on by default).
  const FV = { search: true, project: true, owner: true, status: true, dateRange: true, ...(page?.config?.filters || {}) };
  const seriesDefault = useMemo(() => new Set(Array.isArray(D.seriesOff) ? D.seriesOff : ['prof']), [page]); // eslint-disable-line react-hooks/exhaustive-deps
  const [trendPctManual, setTrendPctManual] = useState(null);  // null = AUTO (follow the optimum); a number = set by hand
  const [trendStats, setTrendStats] = useState({});          // from the per-period chart: key → { reliability, optimalPct }
  // The optimum across the active trends (rounded mean of their optimal %).
  const trendOptimal = useMemo(() => {
    const opt = [...trendKeys].map((k) => trendStats[k]?.optimalPct).filter((v) => typeof v === 'number');
    return opt.length ? Math.round((opt.reduce((a, b) => a + b, 0) / opt.length) / 5) * 5 : null;
  }, [trendKeys, trendStats]);
  const trendPct = trendPctManual == null ? (trendOptimal ?? 0) : trendPctManual;
  const setTrendPct = setTrendPctManual;
  const toggleTrend = (k) => setTrendKeys((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else if (n.size < 2) n.add(k); return n; });
  const trendOpts = useMemo(() => ({ keys: trendKeys, fitTo: trendFitTo, pct: trendPct }), [trendKeys, trendFitTo, trendPct]);
  const onTrendStats = useCallback((st) => setTrendStats(st), []);
  const [filtersOpen, setFiltersOpen] = useState(false);          // collapsed on open; the header row still shows the live selections
  const [scopeOpen, setScopeOpen] = useState(false);
  const [tilesOpen, setTilesOpen] = useState(true);               // the stat tiles row collapses to one line of totals
  const [menu, setMenu] = useState(null);
  const [pickQuery, setPickQuery] = useState('');
  const [pickIdx, setPickIdx] = useState(-1);          // project picker: the highlighted row (-1 = none) — ↑ ↓ move it, Enter ticks it
  // per-table UI state, keyed by table id
  const [open, setOpenMap] = useState({});
  const [expanded, setExpandedMap] = useState({});
  const [sorts, setSorts] = useState({});
  const isOpen = (id) => open[id] !== false;
  const toggleOpen = (id) => setOpenMap((m) => ({ ...m, [id]: !isOpen(id) }));
  const isExpanded = (id) => !!expanded[id];
  const setExpanded = (id, v) => setExpandedMap((m) => ({ ...m, [id]: v }));
  const sortOf = (id, dflt) => sorts[id] || dflt;
  const setSortFor = (id) => (fn) => setSorts((m) => ({ ...m, [id]: fn(m[id]) }));

  // DEV-only fixture mode (see the header). Never true in a production build.
  const demo = useMemo(() => {
    try { return !!import.meta.env.DEV && new URLSearchParams(window.location.search).get('demo') === '1'; } catch { return false; }
  }, []);

  // Theme + viewport — the Work Allocations pattern.
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark');
  useEffect(() => {
    const root = document.documentElement;
    const sync = () => setIsDark(root.getAttribute('data-theme') === 'dark');
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const on = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  const ui = useMemo(() => ({ ...(isDark ? DARK : LIGHT), radius: 12, radiusSm: 8, radiusFull: 999 }), [isDark]);
  // Content width (the tables scale their text to it).
  const wrapRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const el = wrapRef.current; if (!el) return;
    const measure = () => setWrapW(el.clientWidth);
    measure();
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, []);
  // Height of the sticky Filters card — each table's sticky title bar pins
  // just below it.
  const filtersRef = useRef(null);
  const [filtersH, setFiltersH] = useState(0);
  useEffect(() => {
    const el = filtersRef.current; if (!el) return;
    const measure = () => setFiltersH(el.offsetHeight);
    measure();
    window.addEventListener('resize', measure);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    return () => { window.removeEventListener('resize', measure); if (ro) ro.disconnect(); };
  }, [filtersOpen, items]);

  // ---- field wiring: page config over runtime NAME resolution ------------
  useEffect(() => {
    let on = true;
    api.getFields().then((f) => { if (on) setFields(Array.isArray(f) ? f : []); }).catch(() => { if (on) setFields([]); });
    return () => { on = false; };
  }, [api]);
  const resolveField = useCallback((want) => {
    if (!want) return null;
    if (/^customfield_\d+$/.test(want) || ['duedate', 'assignee', 'timespent'].includes(want)) return want; // already an id
    const target = normName(want);
    const hit = (fields || []).find((f) => normName(f.name) === target);
    return hit ? hit.id : null;
  }, [fields]);
  const cfg = useMemo(() => {
    const c = pageCfg || {};
    const kinds = KINDS.map((k) => ({ ...k, names: c[k.cfgKey] || k.types, types: lower(c[k.cfgKey] || k.types) }));
    return {
      value: resolveField(c.valueField || NAME_VALUE),
      supplier: resolveField(c.supplierField || NAME_SUPPLIER),
      client: resolveField(c.clientField || NAME_CLIENT),
      hours: resolveField(c.hoursField || NAME_HOURS),
      kinds,
      kindOfType: Object.fromEntries(kinds.flatMap((k) => k.types.map((t) => [t, k.id]))),
      projectTypes: lower(c.projectTypes || DEFAULT_PROJECT_TYPES),
      excludedStatuses: lower(c.excludedStatuses || DEFAULT_EXCLUDED_STATUSES),
      paidStatuses: lower(c.paidStatuses || DEFAULT_PAID_STATUSES),
      invoicedStatuses: lower(c.invoicedStatuses || DEFAULT_INVOICED_STATUSES),
      statusOrder: c.debtorStatusOrder || DEFAULT_STATUS_ORDER,
      hiddenStatuses: lower(c.hiddenStatuses || DEFAULT_HIDDEN_STATUSES),
      hiddenStatusNames: c.hiddenStatuses || DEFAULT_HIDDEN_STATUSES,
      allTypeNames: [...kinds.flatMap((k) => k.names), ...(c.projectTypes || DEFAULT_PROJECT_TYPES)],
    };
  }, [pageCfg, resolveField]);
  const fieldsReady = fields !== null;
  const kindOf = useCallback((it) => cfg.kindOfType[typeOf(it).toLowerCase()] || null, [cfg]);
  const isProjectItem = useCallback((it) => cfg.projectTypes.includes(typeOf(it).toLowerCase()), [cfg]);
  const valOf = useCallback((it) => numOf(it, cfg.value), [cfg]);
  const kindDef = (id) => cfg.kinds.find((k) => k.id === id);
  const kindColor = (id) => ui[kindDef(id)?.colorKey || 'blue'];

  // The Debtors WORKFLOW's ordered status list — the Debtors per Status
  // matrix shows one column per status (excludedStatuses dropped), so
  // every workflow state is visible even when no item sits in it.
  const debtorTypeNames = useMemo(() => kindDef('deb')?.names || ['Debtors'], [cfg]); // eslint-disable-line react-hooks/exhaustive-deps
  const [wfStatuses, setWfStatuses] = useState(null);
  useEffect(() => {
    let on = true;
    Promise.all(debtorTypeNames.map((t) => api.getIssueTypeStatuses(t).catch(() => null)))
      .then((lists) => {
        if (!on) return;
        const seen = new Set(); const out = [];
        lists.forEach((l) => {
          const arr = Array.isArray(l) ? l : (l?.statuses || []);
          arr.forEach((s) => {
            const nm = (s && (s.name || s.statusName)) || (typeof s === 'string' ? s : '');
            if (nm && !seen.has(nm.toLowerCase())) { seen.add(nm.toLowerCase()); out.push(nm); }
          });
        });
        setWfStatuses(out);
      });
    return () => { on = false; };
  }, [api, debtorTypeNames]);

  // ---- loads (wait for field resolution so the field lists are complete) --
  const fetchAll = useCallback(async (jql, fieldList, cap = MAX_ROWS) => {
    const out = []; let startAt = 0;
    for (;;) {
      const res = await api.searchIssues(jql, fieldList, PAGE, null, startAt, true);
      const issues = res?.issues || res?.data?.issues || (Array.isArray(res) ? res : []) || [];
      out.push(...issues);
      if (issues.length < PAGE || out.length >= cap) break;
      startAt += issues.length;
    }
    if (out.length >= cap) out.truncated = true;   // the caller decides what a hit means — never a silent drop
    return out;
  }, [api]);
  const baseZql = String(pageCfg.baseZql || '').trim();
  const withBase = (q) => (baseZql ? `(${baseZql}) AND (${q})` : q);
  const financeJql = useMemo(() => `${withBase(`type in (${cfg.allTypeNames.map((t) => `"${t.replace(/"/g, '\\"')}"`).join(', ')})`)} order by duedate asc`, [cfg, baseZql]); // eslint-disable-line react-hooks/exhaustive-deps
  const hoursJql = useMemo(() => (cfg.hours ? `${withBase(`${cfg.hours} > 0`)} order by duedate asc` : null), [cfg, baseZql]); // eslint-disable-line react-hooks/exhaustive-deps
  // Quick filter buttons (admin-defined caption + ZQL, plus the standard
  // five every dashboard carries — customs/standardButtons.js): a PRESSED
  // button resolves to a key set with ONE keys-only call —
  // `(<baseZql>) AND (<zql>)`. A button nobody pressed costs nothing
  // (egress, 2026-09-29).
  const filterButtons = useMemo(() => withStandardButtons(Array.isArray(pageCfg.filterButtons) ? pageCfg.filterButtons.filter((d) => d && d.id && d.zql) : []), [pageCfg]);
  const [keySets, setKeySets] = useState({});          // button id → Set of keys | null (loading) | { error }
  const [buttonsOn, setButtonsOn] = useState(() => new Set((Array.isArray(page?.config?.filterButtons) ? page.config.filterButtons : []).filter((d) => d && d.on).map((d) => d.id)));
  const keyCache = useRef(new Map());
  useEffect(() => {
    if (!fieldsReady) return undefined;
    let on = true;
    // Only the keys this page can use: the finance work types, plus the
    // hours rows. A saved filter that matches the whole site (60k+ items)
    // would otherwise blow past any cap — and a cap hit used to hide data.
    const relevant = `(type in (${cfg.allTypeNames.map((t) => `"${t.replace(/"/g, '\\"')}"`).join(', ')}))`;
    filterButtons.filter((d) => buttonsOn.has(d.id)).forEach((d) => {
      const q = withBase(`(${d.zql}) AND ${relevant}`);
      const cached = keyCache.current.get(q);
      if (cached) { setKeySets((m) => (m[d.id] === cached ? m : { ...m, [d.id]: cached })); return; }
      setKeySets((m) => ({ ...m, [d.id]: null }));
      fetchAll(`${q} order by key asc`, 'key', MAX_KEYS).then((rows) => {
        const set = new Set(rows.map((r) => r.key));
        if (rows.truncated) set.truncated = rows.length;       // reported on the button and in the audit
        keyCache.current.set(q, set);
        if (on) setKeySets((m) => ({ ...m, [d.id]: set }));
      }).catch((e) => { if (on) setKeySets((m) => ({ ...m, [d.id]: { error: e?.response?.data?.error || e?.message || 'ZQL failed' } })); });
    });
    return () => { on = false; };
  }, [fieldsReady, filterButtons, buttonsOn, baseZql, fetchAll, cfg.allTypeNames, cfg.hours]); // eslint-disable-line react-hooks/exhaustive-deps
  const defState = (id) => { const k = keySets[id]; return k instanceof Set ? 'ready' : k && k.error ? 'error' : k === undefined && !buttonsOn.has(id) ? 'idle' : 'loading'; };
  const toggleButton = (id) => setButtonsOn((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const FINANCE_FIELDS = useMemo(() => ['summary', 'issuetype', 'status', 'duedate', 'assignee', ...(cfg.value ? [cfg.value] : []), ...(cfg.supplier ? [cfg.supplier] : [])].join(','), [cfg]);
  const HOURS_FIELDS = useMemo(() => ['issuetype', 'status', 'duedate', 'assignee', ...(cfg.hours ? [cfg.hours] : []), ...(cfg.client ? [cfg.client] : [])].join(','), [cfg]);

  useEffect(() => {
    if (!fieldsReady) return;
    let on = true;
    setError(null); setHoursError(null); setItems(null); setHoursItems(null);
    (async () => {
      let projs = [];
      try {
        const p = await api.getProjects();
        projs = (Array.isArray(p) ? p : []).map((x) => ({ key: x.key || x.projectKey, name: x.name || x.projectName || (x.key || x.projectKey) })).filter((x) => x.key);
        if (on) setProjects(projs);
      } catch { /* the project list only names rows */ }
      if (import.meta.env.DEV && demo) {   // literal guard: the build drops this block AND the chunk
        const { buildDemo } = await import('./demoData');
        const d = buildDemo({ projects: projs, valueField: cfg.value || 'customfield_10071', supplierField: cfg.supplier || 'customfield_10065', clientField: cfg.client || 'customfield_10110', hoursField: cfg.hours || 'customfield_10037' });
        if (on) { setItems(d.finance); setHoursItems(d.hours); }
        return;
      }
      try {
        const rows = await fetchAll(financeJql, FINANCE_FIELDS);
        if (on) setItems(rows);
      } catch (e) {
        if (on) { setError(e?.response?.data?.error || e?.message || 'Could not load finance items.'); setItems([]); }
      }
      if (on) setHoursItems([]);                                  // the Invoiceable Work section was retired 2026-09-21
      return;
      try {
        const rows = await fetchAll(hoursJql, HOURS_FIELDS);
        if (on) setHoursItems(rows);
      } catch (e) {
        if (on) { setHoursError(e?.response?.data?.error || e?.message || 'Could not load the invoiceable work.'); setHoursItems([]); }
      }
    })();
    return () => { on = false; };
  }, [api, fieldsReady, demo, fetchAll, financeJql, hoursJql, FINANCE_FIELDS, HOURS_FIELDS, cfg]);

  // Per-project facts from the project's "Project" WORK ITEM(S): due date,
  // the Done-category flag (the Hide Done Projects filter), the owners
  // (each Project item's Assigned resource — the Project Owner filter) and
  // how many Project items the project carries (more than one is flagged
  // in the filter menus).
  const projectMeta = useMemo(() => {
    const m = {};
    (items || []).forEach((it) => {
      if (!isProjectItem(it)) return;
      const pk = projOf(it); if (!pk) return;
      const meta = m[pk] || (m[pk] = { due: '', done: false, owners: [], projectItems: 0, keys: [] });
      meta.projectItems += 1; meta.keys.push(it.key);
      if (!meta.due && dueOf(it)) meta.due = dueOf(it);
      if (statusCat(it) === 'done') meta.done = true;
      const who = assigneeOf(it);
      if (who && !meta.owners.includes(who)) meta.owners.push(who);
    });
    return m;
  }, [items, isProjectItem]);
  const multiProjectItems = useMemo(() => Object.entries(projectMeta).filter(([, m]) => m.projectItems > 1).map(([k, m]) => ({ key: k, ...m })), [projectMeta]);
  const ownerOptions = useMemo(() => {
    const by = new Map();
    Object.values(projectMeta).forEach((m) => m.owners.forEach((o) => by.set(o, (by.get(o) || 0) + 1)));
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([name, projects]) => ({ name, projects }));
  }, [projectMeta]);

  // ---- the filter pipeline: items → financeItems → viewed → filtered ----
  const financeItems = useMemo(() => (items || []).filter((it) => kindOf(it)), [items, kindOf]);
  // Every status present on the finance rows, lifecycle order first, then
  // alphabetical — the Status filter's options.
  const statusOptions = useMemo(() => {
    const seen = new Map();
    financeItems.forEach((it) => { const n = statusName(it); const k = n.toLowerCase(); if (!seen.has(k)) seen.set(k, { key: k, label: n, count: 0 }); seen.get(k).count += 1; });
    const rank = (k) => { const i = cfg.statusOrder.findIndex((o) => o.toLowerCase() === k); return i < 0 ? 999 : i; };
    return [...seen.values()].sort((a, b) => rank(a.key) - rank(b.key) || a.label.localeCompare(b.label));
  }, [financeItems, cfg.statusOrder]);
  const viewed = useMemo(() => (viewKind ? financeItems.filter((it) => kindOf(it) === viewKind) : financeItems), [financeItems, viewKind, kindOf]);
  const projectOpts = useMemo(() => {
    const present = new Set([...financeItems.map(projOf), ...(hoursItems || []).map(projOf)].filter(Boolean));
    const named = new Map(projects.map((p) => [p.key, p.name]));
    return [...present].sort().map((k) => ({ key: k, name: named.get(k) || k }));
  }, [projects, financeItems, hoursItems]);
  const projNameOf = useCallback((k) => {
    const p = projectOpts.find((o) => o.key === k);
    return p && p.name !== p.key ? p.name : null;
  }, [projectOpts]);
  // Scope shared by BOTH loads: project · owner · status · quick buttons ·
  // date range. `whyOut(it)` names the FIRST rule that drops an item (the
  // gap audit reads it); `inScope` is just "no reason".
  const whyOut = useCallback((it) => {
    const pk = projOf(it);
    if (projectSel.size && !projectSel.has(pk)) return 'Project filter';
    for (const id of buttonsOn) { const k = keySets[id]; if (k instanceof Set && !k.truncated && !k.has(it.key)) return `Quick button “${(filterButtons.find((d) => d.id === id) || {}).caption || id}”`; }
    if (statusOff.size && statusOff.has(statusName(it).toLowerCase())) return `Status “${statusName(it)}” deselected in the Status filter`;
    if (ownerSel.size && !(projectMeta[pk]?.owners || []).some((o) => ownerSel.has(o))) return 'Project Owner filter';
    const d = dueOf(it);
    if (dateFrom || dateTo) {
      if (!d) return 'No due date — a due-date range is selected (shows under “All dates”)';
      if (dateFrom && d < dateFrom) return `Due date before the range (before ${dateFrom})`;
      if (dateTo && d > dateTo) return `Due date after the range (after ${dateTo})`;
    }
    return null;
  }, [projectMeta, projectSel, statusOff, ownerSel, dateFrom, dateTo, buttonsOn, keySets, filterButtons]);
  const inScope = useCallback((it) => whyOut(it) === null, [whyOut]);
  const matchesSearch = useCallback((it) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const pk = projOf(it);
    return `${it.key} ${it.fields?.summary || ''} ${statusName(it)} ${typeOf(it)} ${projNameOf(pk) || ''} ${optOf(it, cfg.supplier)}`.toLowerCase().includes(q);
  }, [search, projNameOf, cfg.supplier]);
  const filtered = useMemo(() => viewed.filter((it) => inScope(it) && matchesSearch(it)), [viewed, inScope, matchesSearch]);
  const undatedCount = useMemo(() => viewed.filter((it) => !dueOf(it)).length, [viewed]);
  const hiddenStatusCount = useMemo(() =>
    statusOff.size ? viewed.filter((it) => statusOff.has(statusName(it).toLowerCase())).length : 0,
  [viewed, statusOff]);

  // ---- aggregations (all from `filtered` — filters pull through) ---------
  const zeroKinds = () => Object.fromEntries(KINDS.map((k) => [k.id, 0]));
  const totals = useMemo(() => {
    const t = zeroKinds();
    filtered.forEach((it) => { t[kindOf(it)] += valOf(it) || 0; });
    return t;
  }, [filtered, kindOf, valOf]);

  // Per-project rows; has[kind] distinguishes "no items" (–) from a 0.
  const projectRows = useMemo(() => {
    const by = new Map();
    filtered.forEach((it) => {
      const pk = projOf(it) || '—';
      let r = by.get(pk); if (!r) { r = { key: pk, v: zeroKinds(), has: {}, items: {}, due: projectMeta[pk]?.due || '' }; by.set(pk, r); }
      const k = kindOf(it);
      r.v[k] += valOf(it) || 0; r.has[k] = true; (r.items[k] = r.items[k] || []).push(it);
    });
    return [...by.values()];
  }, [filtered, kindOf, valOf, projectMeta]);
  const profitOf = (v) => (v.pinc || 0) - (v.pexp || 0);                              // Projected Profit
  const actualOf = (v) => (v.deb || 0) - ((v.exp || 0) + (v.sal || 0));              // Actual Profit
  const summarySort = sortOf('summary', { key: 'mag', dir: 'desc' });
  const sortedSummary = useMemo(() => {
    const rows = [...projectRows];
    const s = summarySort;
    const get = s.key === 'mag' ? (r) => KINDS.reduce((a, k) => a + r.v[k.id], 0)
      : s.key === 'project' ? (r) => (projNameOf(r.key) || r.key).toLowerCase()
        : s.key === 'due' ? (r) => r.due || ''
          : s.key === 'prof' ? (r) => profitOf(r.v)
            : s.key === 'act' ? (r) => actualOf(r.v)
              : (r) => r.v[s.key] || 0;
    const mul = s.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const va = get(a), vb = get(b);
      if (s.key === 'due') { if (!va && !vb) return 0; if (!va) return 1; if (!vb) return -1; }
      return (typeof va === 'string' ? va.localeCompare(vb) : va - vb) * mul;
    });
    return rows;
  }, [projectRows, summarySort, projNameOf]);

  // A projects × columns money matrix from a subset of the filtered rows.
  // `colOf(it)` names the column; `order` (optional) fixes column order,
  // anything else found on items appends after it.
  const matrixOf = useCallback((rowsIn, colOf, order) => {
    const rows = new Map();
    const found = new Map();                           // key → label
    rowsIn.forEach((it) => {
      const c = colOf(it); if (c == null) return;
      const ck = String(c.key); if (!found.has(ck)) found.set(ck, c.label);
      const pk = projOf(it) || '—';
      let r = rows.get(pk); if (!r) { r = { key: pk, cols: {}, items: {}, all: [], total: 0 }; rows.set(pk, r); }
      const v = valOf(it) || 0;
      r.cols[ck] = (r.cols[ck] || 0) + v; (r.items[ck] = r.items[ck] || []).push(it); r.all.push(it);
      r.total += v;
    });
    const cols = []; const seen = new Set();
    (order || []).forEach((c) => { if (!seen.has(c.key)) { seen.add(c.key); cols.push({ key: c.key, label: found.get(c.key) || c.label }); } });
    [...found.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([k, label]) => { if (!seen.has(k)) { seen.add(k); cols.push({ key: k, label }); } });
    return { rows: [...rows.values()], cols };
  }, [valOf]);

  // Debtors per Status — one column per Debtors-workflow status, in
  // workflow order, excludedStatuses dropped.
  const debtorRows = useMemo(() => filtered.filter((it) => kindOf(it) === 'deb' && !cfg.excludedStatuses.includes(statusName(it).toLowerCase())), [filtered, kindOf, cfg.excludedStatuses]);
  // Columns: the lifecycle order first (only the statuses the workflow
  // actually has, unless the lookup failed), then any other workflow
  // status; a status found only on items appends inside matrixOf.
  const statusColumns = useMemo(() => {
    const gone = (s) => cfg.excludedStatuses.includes(s.toLowerCase()) || statusOff.has(s.toLowerCase());
    const wf = (wfStatuses || []).filter((s) => !gone(s));
    const seen = new Set(); const out = [];
    const push = (s) => { const k = s.toLowerCase(); if (!seen.has(k)) { seen.add(k); out.push({ key: k, label: wf.find((w) => w.toLowerCase() === k) || s }); } };
    cfg.statusOrder.forEach((s) => { if (!gone(s) && (!wf.length || wf.some((w) => w.toLowerCase() === s.toLowerCase()))) push(s); });
    wf.forEach(push);
    return out;
  }, [wfStatuses, cfg.excludedStatuses, cfg.statusOrder, statusOff]);
  const byStatus = useMemo(() => matrixOf(debtorRows,
    (it) => ({ key: statusName(it).toLowerCase(), label: statusName(it) }), statusColumns),
  [debtorRows, matrixOf, statusColumns]);

  // Month columns for the invoice matrices: the selected range when both
  // ends are set, else the span of the rows' due dates; 'None' leads when
  // an undated row exists.
  const monthCols = useCallback((rowsIn) => {
    let months;
    if (dateFrom && dateTo) months = monthsBetween(dateFrom, dateTo);
    else {
      const ds = rowsIn.map(dueOf).filter(Boolean).sort();
      months = ds.length ? monthsBetween(ds[0], ds[ds.length - 1]) : [];
    }
    const cols = months.map((m) => ({ key: m, label: fmtMonth(m) }));
    if (rowsIn.some((it) => !dueOf(it))) cols.unshift({ key: NONE, label: 'None' });
    return cols;
  }, [dateFrom, dateTo]);
  const monthOf = (it) => { const d = dueOf(it); return d ? { key: d.slice(0, 7), label: fmtMonth(d.slice(0, 7)) } : { key: NONE, label: 'None' }; };
  const notPaidRows = useMemo(() => debtorRows.filter((it) => !cfg.paidStatuses.includes(statusName(it).toLowerCase())), [debtorRows, cfg.paidStatuses]);
  const notInvoicedRows = useMemo(() => notPaidRows.filter((it) => !cfg.invoicedStatuses.includes(statusName(it).toLowerCase())), [notPaidRows, cfg.invoicedStatuses]);
  const notPaid = useMemo(() => matrixOf(notPaidRows, monthOf, monthCols(notPaidRows)), [notPaidRows, matrixOf, monthCols]);
  const notInvoiced = useMemo(() => matrixOf(notInvoicedRows, monthOf, monthCols(notInvoicedRows)), [notInvoicedRows, matrixOf, monthCols]);

  // Expenses per Supplier — Procurement Orders by Supplier List.
  const supplierAgg = useMemo(() => {
    const by = new Map();
    filtered.forEach((it) => {
      if (kindOf(it) !== 'exp') return;
      const name = optOf(it, cfg.supplier) || 'None';
      let r = by.get(name); if (!r) { r = { key: name, total: 0, count: 0, points: [], items: [] }; by.set(name, r); }
      const v = valOf(it) || 0;
      r.total += v; r.count += 1; r.points.push({ d: dueOf(it), v }); r.items.push(it);
    });
    const rows = [...by.values()].sort((a, b) => b.total - a.total);
    // charted: the suppliers picked in the list, else the top SUPPLIER_LINES
    const picked = supplierSel.size ? rows.filter((r) => supplierSel.has(r.key)) : rows.slice(0, SUPPLIER_LINES);
    rows.forEach((r, i) => { r.rank = i + 1; r.color = null; });
    picked.forEach((r, i) => { r.color = PALETTE[i % PALETTE.length]; });
    return rows;
  }, [filtered, kindOf, valOf, cfg.supplier, supplierSel]);
  const chartedSuppliers = useMemo(() => supplierAgg.filter((r) => r.color), [supplierAgg]);
  const supplierSeries = useMemo(() => chartedSuppliers.map((r) => ({ key: r.key, label: r.key, color: r.color })), [chartedSuppliers]);
  const supplierPoints = useMemo(() => chartedSuppliers.flatMap((r) => r.points.map((p) => ({ d: p.d, v: { [r.key]: p.v } }))), [chartedSuppliers]);
  // A period of the supplier chart was clicked: that period's orders of the
  // suppliers showing (a point = that supplier only).
  const pickFromSupplierChart = ({ start, bucket, keys, whole }) => {
    const vis = new Set(keys);
    const items = chartedSuppliers.filter((r) => vis.has(r.key)).flatMap((r) => r.items).filter((it) => {
      const d = dueOf(it); return (d ? bucketStartOf(d, bucket) : NONE) === start;
    });
    const period = start === NONE ? 'No due date' : bucketLabel(start, bucket);
    openInSearch(items, whole ? `Suppliers · ${period}` : `${[...vis].join(', ')} · ${period}`);
  };
  const supplierSort = sortOf('supplier', { key: 'total', dir: 'desc' });
  const sortedSuppliers = useMemo(() => {
    const rows = [...supplierAgg];
    const mul = supplierSort.dir === 'asc' ? 1 : -1;
    const get = supplierSort.key === 'supplier' ? (r) => r.key.toLowerCase() : supplierSort.key === 'count' ? (r) => r.count : (r) => r.total;
    rows.sort((a, b) => { const va = get(a), vb = get(b); return (typeof va === 'string' ? va.localeCompare(vb) : va - vb) * mul; });
    return rows;
  }, [supplierAgg, supplierSort]);

  // Summary of Invoiceable Work — Client Name × Assigned Resource, hours.
  const hoursScoped = useMemo(() => (hoursItems || []).filter(inScope), [hoursItems, inScope]);
  const hoursMatrix = useMemo(() => {
    const rows = new Map(); const people = new Map();
    hoursScoped.forEach((it) => {
      const h = numOf(it, cfg.hours) || 0; if (!h) return;
      const client = optOf(it, cfg.client) || 'None';
      const who = assigneeOf(it) || 'Unassigned';
      let r = rows.get(client); if (!r) { r = { key: client, cols: {}, items: {}, all: [], total: 0 }; rows.set(client, r); }
      r.cols[who] = (r.cols[who] || 0) + h; r.total += h; (r.items[who] = r.items[who] || []).push(it); r.all.push(it);
      people.set(who, (people.get(who) || 0) + h);
    });
    const cols = [...people.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => ({ key: k, label: k }));
    return { rows: [...rows.values()], cols };
  }, [hoursScoped, cfg.hours, cfg.client]);

  // Chart points — one per filtered item: its due date and the rand it
  // contributes to each series. 'texp' = Expenses + Salaries (the legacy
  // "Total Expenses - INCL Salaries & Overtime").
  // 'prof' = Projected Profit = Projected Income − Projected Expense;
  // 'act' = Actual Profit = Debtors − (Expenses + Salaries) — the column
  // overlays on their own axis.
  const chartPoints = useMemo(() => filtered.map((it) => {
    const v = valOf(it) || 0; const k = kindOf(it);
    return { d: dueOf(it), v: {
      pexp: k === 'pexp' ? v : 0, texp: (k === 'exp' || k === 'sal') ? v : 0, deb: k === 'deb' ? v : 0, pinc: k === 'pinc' ? v : 0, sal: k === 'sal' ? v : 0,
      prof: k === 'pinc' ? v : k === 'pexp' ? -v : 0,
      act: k === 'deb' ? v : (k === 'exp' || k === 'sal') ? -v : 0,
    } };
  }), [filtered, valOf, kindOf]);
  const PROFIT_BARS = useMemo(() => [
    { key: 'prof', label: 'Projected Profit', posColor: ui.lightBlue, negColor: ui.pink, opacity: 0.45, info: INFO_PROJECTED },
    { key: 'act', label: 'Actual Profit', posColor: ui.green, negColor: ui.red, opacity: 0.7, info: INFO_ACTUAL },
  ], [ui]);
  // The two chart "modes" for the Theoretical Projections / Actuals toggle.
  const PROJECTION_KEYS = ['pexp', 'pinc', 'prof'];
  const ACTUAL_KEYS = ['deb', 'texp', 'sal', 'act'];
  const CHART_SERIES = useMemo(() => [
    { key: 'pexp', label: 'Projected Expense', color: ui.rose },
    { key: 'texp', label: 'Total Expenses — INCL Salaries & Overtime', color: ui.red },
    { key: 'deb', label: 'Debtors', color: ui.teal },
    { key: 'pinc', label: 'Projected Income', color: ui.mint },
    { key: 'sal', label: 'Salaries & Overtime Only', color: ui.orange },
  ], [ui]);

  // ---- styles (the Finance-custom S object; the wrap is FULL WIDTH) -----
  const S = useMemo(() => ({
    ui,
    scroll: { height: '100%', overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable', background: ui.page, color: ui.fg, fontFamily: FONT },
    wrap: { width: '100%', boxSizing: 'border-box', padding: narrow ? '14px 10px 24px' : '18px 22px 28px' },
    h1: { fontSize: narrow ? 18 : 20, margin: 0, fontWeight: 700, lineHeight: 1.2, letterSpacing: -0.2 },
    label: { display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', margin: '0 0 6px', color: ui.muted, textTransform: 'uppercase' },
    tiny: { fontSize: 12, color: ui.muted },
    input: { width: '100%', padding: '9px 12px', borderRadius: ui.radiusSm, border: `1px solid ${ui.border}`, background: ui.card, color: ui.fg, fontSize: 13.5, boxSizing: 'border-box', outline: 'none', fontFamily: FONT, colorScheme: isDark ? 'dark' : 'light' },
    card: { border: `1px solid ${ui.border}`, borderRadius: ui.radius, background: ui.card, boxShadow: ui.shadowSm, padding: 16 },
    pill: (on) => ({ fontSize: 12.5, fontWeight: 700, padding: '5px 12px', borderRadius: ui.radiusFull, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${on ? ui.primary : ui.border}`, background: on ? `${ui.primary}14` : ui.card, color: on ? ui.fg : ui.muted, fontFamily: FONT }),
    muted: { color: ui.muted, fontSize: 12.5 },
    th: { textAlign: 'left', fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted, padding: '10px 12px', borderBottom: `1px solid ${ui.border}`, borderRight: `1px solid ${ui.border}`, whiteSpace: 'nowrap', background: ui.card },
    td: { padding: '9px 12px', borderBottom: `1px solid ${ui.border}`, borderRight: `1px solid ${ui.border}`, verticalAlign: 'middle', fontSize: 13.5 },
    num: { textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
    exportBtn: { fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: 999, cursor: 'pointer', whiteSpace: 'nowrap', border: `1px solid ${ui.border}`, background: ui.card, color: ui.muted, fontFamily: FONT },
  }), [ui, isDark, narrow]);

  // Tables scale their text down (to 70%) toward the page width; past that
  // the table scrolls sideways inside its card (the wide month × project
  // and person × client matrices). The title bar is sticky under the
  // Filters card.
  // Two-up: charts and the invoice matrices pair up horizontally at every
  // width except a phone.
  const twoUp = !narrow;
  const pairStyle = { display: 'grid', gridTemplateColumns: twoUp ? 'minmax(0, 1fr) minmax(0, 1fr)' : 'minmax(0, 1fr)', gap: twoUp ? 14 : 0, alignItems: 'start' };
  // The two charts side by side, 480px tall (a phone stacks them at 300px).
  const chartPairStyle = pairStyle;
  const chartSide = twoUp;
  const chartH = narrow ? 300 : 480;
  const halfW = twoUp ? (wrapW - 14) / 2 : wrapW;
  const tableFit = useCallback((needed, availW = wrapW) => {
    const f = availW ? Math.min(1, Math.max(0.7, availW / needed)) : 1;
    const r2 = (v) => Math.round(v * 100) / 100;
    return {
      f,
      td: { ...S.td, fontSize: r2(13.5 * f), padding: `${r2(9 * f)}px ${r2(12 * f)}px` },
      th: { ...S.th, whiteSpace: 'normal', lineHeight: 1.2, verticalAlign: 'bottom', fontSize: r2(Math.max(9, 11 * f)), padding: `${r2(10 * f)}px ${r2(12 * f)}px` },
      bar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '12px 14px 10px', position: 'sticky', top: narrow ? 0 : filtersH, zIndex: 4, background: ui.card },
    };
  }, [wrapW, S, narrow, filtersH, ui]);

  // Click-to-sort column header: ▲ / ▼ shows the active direction.
  const sortableTh = (T, label, key, sort, setSort, numeric, extra) => (
    <th key={key} onClick={() => setSort((cur) => nextSort(cur, key, numeric))}
      title="Click to sort — click again to flip the direction"
      style={{ ...T.th, textAlign: numeric ? 'right' : 'left', cursor: 'pointer', userSelect: 'none', ...(extra || {}) }}>
      {label}{sort.key === key ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
  const titleBar = (id, title, T, right) => (
    <div style={T.bar}>
      <button onClick={() => toggleOpen(id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flex: 1, minWidth: 200, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: FONT, color: ui.fg }}>
        <span style={{ fontSize: 12, transform: isOpen(id) ? 'rotate(90deg)' : 'none', transition: 'transform .12s', display: 'inline-block' }}>▸</span>
        <span style={{ fontSize: 15, fontWeight: 800 }}>{title}</span>
      </button>
      {isOpen(id) && right}
    </div>
  );
  const exportButton = (onClick, enabled) => (
    <button onClick={onClick} disabled={!enabled} title="Download this table as currently filtered and sorted" style={{ ...S.exportBtn, opacity: enabled ? 1 : 0.5 }}>Export to Excel</button>
  );
  const projectCell = (T, r, extra) => {
    const nm = projNameOf(r.key);
    return <td style={{ ...T.td, whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 160, ...(extra || {}) }} title={r.key}>{nm || r.key}</td>;
  };
  // Open a set of work items in Zebra Search (/issues?jql=key in (…)).
  // Up to 1,000 keys go in the URL; a bigger slice opens its first 1,000.
  // Opens in a NEW Stride tab (the app shell's own tab strip, via useTab().openTab)
  // — the dashboard tab and everything set on it stay exactly as they are.
  const openInSearch = (items, label) => {
    const keys = [...new Set((items || []).map((it) => it && it.key).filter(Boolean))];
    if (!keys.length) return;
    const jql = `key in (${keys.slice(0, 1000).join(', ')}) order by duedate asc`;
    const url = `/issues?jql=${encodeURIComponent(jql)}`;
    const title = `Search · ${label || `${keys.length} items`}`.slice(0, 60);
    if (typeof openTab === 'function') openTab(url, title);
    else window.open(url, '_blank', 'noopener,noreferrer');
  };
  // A clickable number cell: opens its items; never toggles the row's project filter.
  const doorTd = (style, items, content, what) => (
    <td style={{ ...style, cursor: items && items.length ? 'pointer' : 'default' }}
      title={items && items.length ? `Open ${items.length.toLocaleString()} work item${items.length === 1 ? '' : 's'}${what ? ` — ${what}` : ''} in Zebra Search` : undefined}
      onClick={(e) => { if (!items || !items.length) return; e.stopPropagation(); openInSearch(items, what); }}>{content}</td>
  );
  // A chart period was clicked: every filtered item in that bucket whose
  // kind feeds one of the series showing at the time.
  const KIND_SERIES = { pexp: ['pexp', 'prof'], pinc: ['pinc', 'prof'], deb: ['deb', 'act'], exp: ['texp', 'act'], sal: ['texp', 'sal', 'act'] };
  const pickFromChart = ({ start, bucket, keys, whole }) => {
    const vis = new Set(keys);
    const items = filtered.filter((it) => {
      const d = dueOf(it); const bs = d ? bucketStartOf(d, bucket) : NONE;
      if (bs !== start) return false;
      return (KIND_SERIES[kindOf(it)] || []).some((k) => vis.has(k));
    });
    const period = start === NONE ? 'No due date' : bucketLabel(start, bucket);
    const what = whole ? period : `${[...CHART_SERIES, ...PROFIT_BARS].filter((d) => vis.has(d.key)).map((d) => d.label).join(', ')} · ${period}`;
    openInSearch(items, what);
  };
  const rowClick = (r) => {
    const sel = projectSel.has(r.key);
    return {
      onClick: () => toggleProject(r.key),
      title: sel ? 'In the Project filter — click to remove it' : 'Click to add this project to the Project filter',
      style: { cursor: 'pointer', background: sel ? `${ui.blue}0d` : 'transparent' },
      onMouseEnter: (e) => { if (!sel) e.currentTarget.style.background = ui.surface; },
      onMouseLeave: (e) => { e.currentTarget.style.background = sel ? `${ui.blue}0d` : 'transparent'; },
    };
  };
  const showMoreRow = (id, T, cols, rows, restCells) => {
    if (rows.length <= SHOW_TOP) return null;
    if (!isExpanded(id)) {
      return (
        <tr onClick={() => setExpanded(id, true)} title="Show every row" style={{ cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
          <td style={{ ...T.td, color: ui.muted, whiteSpace: 'nowrap' }}>{SHOW_TOP + 1} – {rows.length}</td>
          <td style={{ ...T.td, color: ui.muted, fontWeight: 600 }}><span style={{ fontSize: 11, marginRight: 6 }}>▸</span>Show more…</td>
          {restCells}
        </tr>
      );
    }
    return (
      <tr onClick={() => setExpanded(id, false)} title="Back to the top rows" style={{ cursor: 'pointer' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
        <td colSpan={cols} style={{ ...T.td, color: ui.muted, fontWeight: 600 }}><span style={{ fontSize: 11, marginRight: 6 }}>▾</span>Show fewer</td>
      </tr>
    );
  };

  // ---- the project picker menu -------------------------------------------
  const filterTrigger = (kind, valueLabel, engagedVal) => {
    const openHere = menu && menu.type === kind;
    const engaged = !!engagedVal;
    return (
      <button type="button" onClick={(e) => { e.stopPropagation(); const r = e.currentTarget.getBoundingClientRect(); setPickQuery(''); setPickIdx(-1); setMenu({ type: kind, rect: { left: r.left, right: r.right, top: r.top, bottom: r.bottom, width: r.width } }); }}
        style={{ ...S.input, display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: 'pointer', transition: 'box-shadow .12s, border-color .12s',
          borderColor: (openHere || engaged) ? ui.primary : ui.border, background: engaged ? `${ui.primary}0a` : ui.card,
          boxShadow: openHere ? `0 0 0 3px ${ui.primary}22` : 'none', fontWeight: engaged ? 700 : 400 }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{valueLabel}</span>
        <span style={{ marginLeft: 'auto', color: ui.muted, fontSize: 11 }}>▾</span>
      </button>
    );
  };
  const renderMenu = () => {
    if (!menu) return null;
    const r = menu.rect;
    const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const width = Math.min(Math.max(r.width, menu.type === 'statusfilter' ? 260 : 340), vw - 16);
    let left = r.left; if (left + width > vw - 8) left = Math.max(8, vw - 8 - width);
    const openUp = r.bottom > vh - 320;
    const pos = openUp ? { bottom: vh - r.top + 4 } : { top: r.bottom + 4 };
    const panel = { position: 'fixed', left, ...pos, zIndex: 4001, width, maxWidth: 'calc(100vw - 16px)', background: ui.card, border: `1px solid ${ui.border}`, borderRadius: 10, boxShadow: ui.shadowMd, overflow: 'hidden', fontFamily: FONT };
    const rowStyle = (sel) => ({ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 12px', borderRadius: 7, cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', background: sel ? `${ui.primary}14` : 'transparent' });
    const hoverOn = (e) => { e.currentTarget.style.background = ui.surface; };
    const hoverOff = (sel) => (e) => { e.currentTarget.style.background = sel ? `${ui.primary}14` : 'transparent'; };
    const warn = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, color: ui.amber, background: `${ui.amber}1a`, border: `1px solid ${ui.amber}66`, borderRadius: 999, padding: '1px 7px', whiteSpace: 'nowrap' };
    const multiFlag = (pk) => { const m = projectMeta[pk]; return m && m.projectItems > 1 ? <span style={warn} title={`${pk} has ${m.projectItems} "Project" work items (${m.keys.join(', ')}) — owners: ${m.owners.join(', ') || 'none'}`}>⚠ {m.projectItems} Project items</span> : null; };
    // The multi-Project notice — shown EXPLICITLY at the top of the
    // Project and Project Owner menus when any project carries more than
    // one "Project" work item (its owner is then ambiguous).
    // It is NOT shown in the Project menu any more (2026-09-28): picking a
    // project is the job there, and the notice pushed the list down.
    const multiNotice = multiProjectItems.length > 0 && (
      <div style={{ margin: '6px 6px 2px', padding: '8px 10px', borderRadius: 8, background: `${ui.amber}14`, border: `1px solid ${ui.amber}55`, fontSize: 12, lineHeight: 1.5 }}>
        <div style={{ fontWeight: 800, color: ui.amber }}>⚠ {multiProjectItems.length} project{multiProjectItems.length === 1 ? ' has' : 's have'} more than one “Project” work item</div>
        {multiProjectItems.slice(0, 8).map((m) => (
          <div key={m.key} style={{ color: ui.fg }}><strong>{m.key}</strong>{projNameOf(m.key) ? ` — ${projNameOf(m.key)}` : ''}: {m.projectItems} Project items ({m.keys.join(', ')}) · owner{m.owners.length === 1 ? '' : 's'}: {m.owners.join(', ') || <em>none</em>}</div>
        ))}
        {multiProjectItems.length > 8 && <div style={S.muted}>… and {multiProjectItems.length - 8} more</div>}
      </div>
    );
    const q = pickQuery.trim().toLowerCase();
    const LIMIT = 60;
    let body;
    let onKeys;
    if (menu.type === 'statusfilter') {
      // Multi-select: every status on the dashboard, ticked = shown.
      const opts = q ? statusOptions.filter((o) => o.label.toLowerCase().includes(q)) : statusOptions;
      const allOn = statusOff.size === 0;
      const flip = (k) => setStatusOff((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });
      body = (
        <div style={{ maxHeight: 300, overflowY: 'auto', padding: 6 }}>
          <div onClick={() => setStatusOff(allOn ? new Set(statusOptions.map((o) => o.key)) : new Set())} style={rowStyle(false)} onMouseEnter={hoverOn} onMouseLeave={hoverOff(false)}>
            <input type="checkbox" readOnly checked={allOn} ref={(el) => { if (el) el.indeterminate = !allOn && statusOff.size < statusOptions.length; }} style={{ width: 15, height: 15, margin: 0, accentColor: ui.blue }} />
            <span style={S.muted}>{allOn ? 'All statuses (click to clear all)' : 'Select all statuses'}</span>
          </div>
          {opts.map((o) => { const on = !statusOff.has(o.key); return (
            <div key={o.key} onClick={() => flip(o.key)} style={rowStyle(false)} onMouseEnter={hoverOn} onMouseLeave={hoverOff(false)}>
              <input type="checkbox" readOnly checked={on} style={{ width: 15, height: 15, margin: 0, accentColor: ui.blue, cursor: 'pointer' }} />
              <span style={{ textDecoration: on ? 'none' : 'line-through', opacity: on ? 1 : 0.6 }}>{o.label}</span>
              <span style={{ ...S.muted, marginLeft: 'auto', fontSize: 11 }}>{o.count.toLocaleString()}</span>
            </div>
          ); })}
          {opts.length === 0 && <div style={{ ...S.muted, padding: '8px 12px' }}>No matches.</div>}
        </div>
      );
    } else if (menu.type === 'ownerfilter') {
      const opts = q ? ownerOptions.filter((o) => o.name.toLowerCase().includes(q)) : ownerOptions;
      const shown = opts.slice(0, LIMIT);
      body = (
        <div style={{ maxHeight: 320, overflowY: 'auto', padding: 6 }}>
          {multiNotice}
          <div onClick={() => setOwnerSel(new Set())} style={rowStyle(ownerSel.size === 0)} onMouseEnter={hoverOn} onMouseLeave={hoverOff(ownerSel.size === 0)}>
            <input type="checkbox" readOnly checked={ownerSel.size === 0} style={{ width: 15, height: 15, margin: 0, accentColor: ui.blue }} />
            <span style={S.muted}>All Project Owners{ownerSel.size ? ` (clear ${ownerSel.size})` : ''}</span>
          </div>
          {shown.map((o) => { const on = ownerSel.has(o.name); return (
            <div key={o.name} onClick={() => toggleOwner(o.name)} style={rowStyle(on)} onMouseEnter={hoverOn} onMouseLeave={hoverOff(on)}>
              <input type="checkbox" readOnly checked={on} style={{ width: 15, height: 15, margin: 0, accentColor: ui.blue, cursor: 'pointer' }} />
              {o.name}<span style={{ ...S.muted, marginLeft: 'auto', fontSize: 11 }}>{o.projects} project{o.projects === 1 ? '' : 's'}</span>
            </div>
          ); })}
          {opts.length > LIMIT && <div style={{ ...S.muted, padding: '6px 12px', fontSize: 11 }}>{shown.length} of {opts.length} — keep typing to narrow</div>}
          {opts.length === 0 && <div style={{ ...S.muted, padding: '8px 12px' }}>{ownerOptions.length ? 'No matches.' : 'No “Project” work item carries an Assigned resource.'}</div>}
        </div>
      );
    } else {
      const all = projectOpts.map((p) => ({ value: p.key, label: p.key === p.name ? p.key : `${p.key} — ${p.name}` }));
      const matches = q ? all.filter((o) => o.label.toLowerCase().includes(q)) : all;
      const shown = matches.slice(0, LIMIT);
      // THE KEYBOARD WALKS THE LIST (as the Billable Work project picker
      // does): ↓ and ↑ move the highlight — from the search box or from a
      // row — Enter ticks the highlighted project, Escape closes. The
      // highlight is kept in view as it moves.
      onKeys = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          setPickIdx((k) => Math.max(0, Math.min(shown.length - 1, (k < 0 ? (e.key === 'ArrowDown' ? -1 : shown.length) : k) + (e.key === 'ArrowDown' ? 1 : -1))));
        } else if (e.key === 'Enter') {
          e.preventDefault();
          const o = shown[pickIdx >= 0 ? pickIdx : 0];
          if (o) { toggleProject(o.value); if (pickIdx < 0) setPickIdx(0); }
        } else if (e.key === 'Escape') { e.preventDefault(); setMenu(null); }
      };
      body = (
        <div style={{ maxHeight: 320, overflowY: 'auto', padding: 6 }}>
          <div onClick={() => setProjectSel(new Set())} style={rowStyle(projectSel.size === 0)} onMouseEnter={hoverOn} onMouseLeave={hoverOff(projectSel.size === 0)}>
            <input type="checkbox" readOnly checked={projectSel.size === 0} style={{ width: 15, height: 15, margin: 0, accentColor: ui.blue }} />
            <span style={S.muted}>All projects{projectSel.size ? ` (clear ${projectSel.size})` : ''}</span>
          </div>
          {shown.map((o, i) => { const on = projectSel.has(o.value); const hot = i === pickIdx; return (
            <div key={o.value} ref={(el) => { if (el && hot) el.scrollIntoView({ block: 'nearest' }); }} onClick={() => { setPickIdx(i); toggleProject(o.value); }} onMouseEnter={() => setPickIdx(i)}
              style={{ ...rowStyle(on), background: hot ? ui.surface : on ? `${ui.primary}14` : 'transparent', boxShadow: hot ? `inset 2px 0 0 ${ui.primary}` : 'none' }}>
              <input type="checkbox" readOnly checked={on} style={{ width: 15, height: 15, margin: 0, accentColor: ui.blue, cursor: 'pointer' }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>{multiFlag(o.value)}
            </div>
          ); })}
          {matches.length > LIMIT && <div style={{ ...S.muted, padding: '6px 12px', fontSize: 11 }}>{shown.length} of {matches.length} — keep typing to narrow</div>}
          {matches.length === 0 && <div style={{ ...S.muted, padding: '8px 12px' }}>No matches.</div>}
        </div>
      );
    }
    return (
      <>
        <div onClick={() => setMenu(null)} style={{ position: 'fixed', inset: 0, zIndex: 4000 }} />
        <div style={panel} onClick={(e) => e.stopPropagation()} onKeyDown={onKeys}>
          <div style={{ padding: 8, borderBottom: `1px solid ${ui.border}`, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input autoFocus value={pickQuery} placeholder={onKeys ? 'Search — ↑ ↓ to move, Enter to tick' : 'Search…'} onClick={(e) => e.stopPropagation()} onChange={(e) => { setPickQuery(e.target.value); setPickIdx(-1); }} style={{ ...S.input, padding: '7px 10px', fontSize: 13 }} />
            <button onClick={() => setMenu(null)} style={{ ...S.pill(true), padding: '6px 12px' }}>Done</button>
          </div>
          {body}
        </div>
      </>
    );
  };

  // ---- stat tiles: one per money kind; click to isolate that kind --------
  // One row at every width: the tile's number shrinks (to 60%) as the
  // tiles get narrower, so the rand never clips.
  const tileW = wrapW ? (wrapW - 12 * (cfg.kinds.length - 1)) / cfg.kinds.length : 240;
  const tileF = narrow ? 1 : Math.min(1, Math.max(0.6, tileW / 230));
  const statCard = (k) => {
    const active = viewKind === k.id;
    const color = ui[k.colorKey];
    const tileItems = filtered.filter((it) => kindOf(it) === k.id);
    return (
      <button key={k.id} type="button" onClick={() => setViewKind(active ? '' : k.id)}
        title={active ? 'Filtering on this tile — click to clear' : `Show only ${k.label.toLowerCase()} (the ↗ opens its items without touching the filter)`}
        style={{
          position: 'relative', textAlign: 'left', cursor: 'pointer', flex: narrow ? '1 1 40%' : undefined, minWidth: narrow ? 150 : 0,
          font: 'inherit', fontFamily: FONT, color: 'inherit',
          background: active ? `${color}0f` : ui.card, border: `1px solid ${active ? color : ui.border}`,
          borderRadius: ui.radius, boxShadow: active ? `0 0 0 2px ${color}44, ${ui.shadowSm}` : ui.shadowSm,
          padding: narrow ? '10px 12px' : `${Math.round(12 * tileF)}px ${Math.round(14 * tileF)}px`,
        }}>
        <div style={{ fontSize: narrow ? 15.5 : Math.round(19 * tileF * 10) / 10, fontWeight: 800, lineHeight: 1.1, letterSpacing: -0.3, color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fmtR(totals[k.id]) || 'R 0.00'}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 3 }}>
          <span style={{ ...S.tiny, fontSize: Math.round(12 * Math.max(0.85, tileF) * 10) / 10, fontWeight: 700, color: ui.fg, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{k.label}</span>
          <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); openInSearch(tileItems, k.label); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); openInSearch(tileItems, k.label); } }}
            title={`Open the ${tileItems.length.toLocaleString()} ${k.label} work items in a new Stride tab — the tile filter stays as it is`}
            style={{ flex: '0 0 auto', fontSize: 11, fontWeight: 800, color: color, background: `${color}14`, border: `1px solid ${color}55`, borderRadius: 999, padding: '1px 7px', lineHeight: 1.2, cursor: 'pointer', whiteSpace: 'nowrap' }}>↗ {tileItems.length.toLocaleString()}</span>
        </div>
        <div style={{ ...S.tiny, fontSize: Math.round(12 * Math.max(0.85, tileF) * 10) / 10, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{active ? '✓ filtering — click to clear' : `${k.hint} — click to isolate`}</div>
      </button>
    );
  };

  // ---- the sticky Filters card -------------------------------------------
  const applyThisYear = () => { setDateFrom(yearFromISO()); setDateTo(yearToISO()); setDatePreset('year'); };
  const applyAllDates = () => { setDateFrom(''); setDateTo(''); setDatePreset('all'); };
  const dateLabel = datePreset === 'year' ? 'This Year' : datePreset === 'all' ? 'All dates' : `${dateFrom || '…'} → ${dateTo || '…'}`;
  const defaultOff = useMemo(() => new Set(cfg.hiddenStatuses), [cfg.hiddenStatuses]);
  const statusIsDefault = statusOff.size === defaultOff.size && [...statusOff].every((k) => defaultOff.has(k));
  const buttonsDefault = filterButtons.filter((d) => d.on).map((d) => d.id).sort().join();
  const anyFilter = !!(projectSel.size || ownerSel.size || search.trim() || viewKind || datePreset !== (D.datePreset === 'all' ? 'all' : 'year') || !statusIsDefault || [...buttonsOn].sort().join() !== buttonsDefault);
  const clearFilters = () => { setProjectSel(new Set()); setOwnerSel(new Set()); setSearch(''); setViewKind(''); if (D.datePreset === 'all') applyAllDates(); else applyThisYear(); setStatusOff(new Set(defaultOff)); setButtonsOn(new Set(filterButtons.filter((d) => d.on).map((d) => d.id))); };
  const statusOn = statusOptions.filter((o) => !statusOff.has(o.key)).length;
  const statusLabel = statusOff.size === 0 ? 'All statuses' : statusOn === 0 ? 'No statuses' : `${statusOn} of ${statusOptions.length} statuses`;
  const ownerLabel = ownerSel.size === 0 ? 'All Project Owners' : ownerSel.size === 1 ? [...ownerSel][0] : `${ownerSel.size} Project Owners`;
  const chev = (dir) => (
    <svg width="10" height="10" viewBox="0 0 10 10" style={{ flex: '0 0 auto', transform: dir === 'down' ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>
      <path d="M3 1l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  const projOneLabel = (k) => { const nm = projNameOf(k); return nm ? `${k} — ${nm}` : k; };
  const projLabel = projectSel.size === 0 ? 'All projects' : projectSel.size === 1 ? projOneLabel([...projectSel][0]) : `${projectSel.size} projects`;
  // Selected projects / owners as chips with an × — the easy deselect.
  const chip = (label, onRemove, color) => (
    <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, padding: '3px 6px 3px 10px', borderRadius: 999, border: `1px solid ${color}55`, background: `${color}12`, color: ui.fg, maxWidth: 320 }}>
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <button onClick={onRemove} title="Remove" style={{ width: 18, height: 18, borderRadius: 18, border: 'none', background: `${color}33`, color: ui.fg, cursor: 'pointer', fontSize: 12, fontWeight: 800, lineHeight: 1, padding: 0, fontFamily: FONT }}>×</button>
    </span>
  );
  const chipsRow = (projectSel.size > 0 || ownerSel.size > 0) && (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
      {projectSel.size > 0 && <span style={{ ...S.tiny, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10.5 }}>Projects</span>}
      {[...projectSel].map((k) => chip(projOneLabel(k), () => toggleProject(k), ui.blue))}
      {projectSel.size > 1 && <button onClick={() => setProjectSel(new Set())} style={{ ...S.tiny, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT, padding: 0 }}>clear projects</button>}
      {ownerSel.size > 0 && <span style={{ ...S.tiny, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10.5, marginLeft: projectSel.size ? 8 : 0 }}>Owners</span>}
      {[...ownerSel].map((o) => chip(o, () => toggleOwner(o), ui.purple))}
      {ownerSel.size > 1 && <button onClick={() => setOwnerSel(new Set())} style={{ ...S.tiny, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT, padding: 0 }}>clear owners</button>}
    </div>
  );
  const dateInputStyle = { ...S.input, padding: '7px 10px', fontSize: 13 };
  const code = (s) => <code style={{ background: ui.surface, padding: '1px 5px', borderRadius: 4 }}>{s}</code>;

  // ---- Gap audit (admin) --------------------------------------------------
  // Loads EVERY finance-type item on the site (no scope ZQL, all dates),
  // then names, for each one the dashboard does not show, the FIRST rule
  // that drops it — and lists them by reason, each row a door to Zebra
  // Search. "Why is KEY missing?" does the same for one key, fetched live.
  const [audit, setAudit] = useState(null);         // null | 'busy' | { rows, all, at } | { error }
  const [auditKey, setAuditKey] = useState('');
  const [auditOne, setAuditOne] = useState(null);   // null | 'busy' | { key, reason, detail }
  const shownKeys = useMemo(() => new Set(filtered.map((it) => it.key)), [filtered]);
  const loadedKeys = useMemo(() => new Set((items || []).map((it) => it.key)), [items]);
  const financeTypeSet = useMemo(() => new Set(cfg.allTypeNames.map((t) => t.toLowerCase())), [cfg]);
  const projectTypeSet = useMemo(() => new Set(cfg.projectTypes), [cfg]);
  // Why is this item not on the dashboard? (null = it is shown)
  const gapReason = useCallback((it) => {
    if (shownKeys.has(it.key)) return null;
    const type = typeOf(it).toLowerCase();
    if (projectTypeSet.has(type)) return null;                                            // the Project item itself is not a money row
    if (!financeTypeSet.has(type)) return `Work type “${typeOf(it)}” is not a finance column (Settings ⚙ on the Projects Finance Summary)`;
    if (!loadedKeys.has(it.key)) return baseZql ? 'Excluded by the background scope ZQL (Filters ⚙)' : 'Not loaded — reload the page';
    if (cfg.excludedStatuses.includes(statusName(it).toLowerCase())) return `Status “${statusName(it)}” is in the never-shown list`;
    if (viewKind && kindOf(it) !== viewKind) return `A stat tile is isolating ${(kindDef(viewKind) || {}).label || viewKind}`;
    const w = whyOut(it); if (w) return w;
    if (!matchesSearch(it)) return `Search box “${search.trim()}”`;
    return 'Shown (but not in the current rows?) — reload';
  }, [shownKeys, loadedKeys, financeTypeSet, projectTypeSet, baseZql, cfg, viewKind, whyOut, matchesSearch, search, kindOf]); // eslint-disable-line react-hooks/exhaustive-deps
  const runAudit = async () => {
    setAudit('busy');
    try {
      const typesZql = `type in (${cfg.allTypeNames.map((t) => `"${t.replace(/"/g, '\\"')}"`).join(', ')}) order by duedate asc`;
      const all = await fetchAll(typesZql, FINANCE_FIELDS);
      const by = new Map();
      all.forEach((it) => { const r = gapReason(it); if (!r) return; if (!by.has(r)) by.set(r, []); by.get(r).push(it); });
      const rows = [...by.entries()].map(([reason, list]) => ({ reason, items: list, count: list.length, rand: list.reduce((a, it) => a + (valOf(it) || 0), 0) })).sort((a, b) => b.count - a.count);
      const noMoney = all.filter((it) => !projectTypeSet.has(typeOf(it).toLowerCase()) && shownKeys.has(it.key) && !(valOf(it) > 0 || valOf(it) < 0));
      setAudit({ rows, all: all.length, shown: all.filter((it) => shownKeys.has(it.key)).length, noMoney, at: new Date() });
    } catch (e) { setAudit({ error: e?.response?.data?.error || e?.message || 'Audit failed' }); }
  };
  const auditOneKey = async () => {
    const k = auditKey.trim().toUpperCase(); if (!k) return;
    setAuditOne('busy');
    try {
      const it = await api.getIssue(k, FINANCE_FIELDS);
      if (!it || !it.key) { setAuditOne({ key: k, reason: 'Not found on this Stride site (not cloned, or a typo)' }); return; }
      const reason = gapReason(it);
      setAuditOne({ key: it.key, it, reason: reason || 'Shown — it is on the dashboard in the current filters', detail: `${typeOf(it)} · ${statusName(it)} · due ${dueOf(it) || 'none'} · ${fmtR(valOf(it) || 0)} · project ${projOf(it)}` });
    } catch (e) { setAuditOne({ key: k, reason: e?.response?.status === 404 ? 'Not found on this Stride site (not cloned, or a typo)' : (e?.response?.data?.error || e?.message || 'Lookup failed') }); }
  };
  const auditPanel = (
    <div style={{ borderTop: `1px dashed ${ui.amber}66`, marginTop: 8, paddingTop: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.amber }}>⚠ Gap audit (admin mode)</span>
        <button onClick={runAudit} disabled={audit === 'busy'} style={{ ...S.pill(true), padding: '5px 12px' }}>{audit === 'busy' ? 'Auditing…' : 'Find every finance item NOT on the dashboard'}</button>
        <span style={S.tiny}>· Why is</span>
        <input value={auditKey} onChange={(e) => setAuditKey(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') auditOneKey(); }} placeholder="KEY-123" style={{ ...S.input, width: 150, padding: '5px 9px', fontSize: 12.5 }} />
        <button onClick={auditOneKey} disabled={auditOne === 'busy' || !auditKey.trim()} style={{ ...S.pill(false), padding: '5px 12px' }}>{auditOne === 'busy' ? '…' : 'missing?'}</button>
        {auditOne && auditOne !== 'busy' && (
          <span style={{ ...S.tiny, color: ui.fg }}>
            <strong>{auditOne.key}</strong>: <span style={{ color: /^Shown/.test(auditOne.reason) ? ui.green : ui.red, fontWeight: 700 }}>{auditOne.reason}</span>{auditOne.detail ? ` — ${auditOne.detail}` : ''}
            {auditOne.it && <button onClick={() => openInSearch([auditOne.it], auditOne.key)} style={{ ...S.tiny, marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT, padding: 0 }}>open ↗</button>}
          </span>
        )}
      </div>
      {audit && audit !== 'busy' && audit.error && <div style={{ ...S.tiny, color: ui.red, marginTop: 6 }}>{audit.error}</div>}
      {audit && audit !== 'busy' && !audit.error && (
        <div style={{ marginTop: 8 }}>
          <div style={{ ...S.tiny, marginBottom: 6 }}>{audit.all.toLocaleString()} finance-type items on the site · <strong style={{ color: ui.green }}>{audit.shown.toLocaleString()} shown</strong> · <strong style={{ color: ui.red }}>{(audit.all - audit.shown).toLocaleString()} not shown</strong> in the current filters ({audit.at.toLocaleTimeString()}).</div>
          <table style={{ borderCollapse: 'collapse', fontSize: 12.5, maxWidth: 900 }}>
            <thead><tr><th style={{ ...S.th, padding: '6px 10px' }}>Reason</th><th style={{ ...S.th, padding: '6px 10px', textAlign: 'right' }}>Items</th><th style={{ ...S.th, padding: '6px 10px', textAlign: 'right' }}>ZAR Value</th><th style={{ ...S.th, padding: '6px 10px' }} /></tr></thead>
            <tbody>
              {audit.rows.map((r) => (
                <tr key={r.reason}>
                  <td style={{ ...S.td, padding: '6px 10px', color: ui.red, fontWeight: 700 }}>{r.reason}</td>
                  <td style={{ ...S.td, ...S.num, padding: '6px 10px' }}>{r.count.toLocaleString()}</td>
                  <td style={{ ...S.td, ...S.num, padding: '6px 10px', color: ui.blue }}>{fmtR(r.rand)}</td>
                  <td style={{ ...S.td, padding: '6px 10px', whiteSpace: 'nowrap' }}><button onClick={() => openInSearch(r.items, r.reason)} title={`Open these ${r.count.toLocaleString()} work items in a new Stride tab`} style={{ ...S.pill(false), padding: '3px 10px' }}>Open the tickets ↗</button></td>
                </tr>
              ))}
              {audit.rows.length === 0 && <tr><td colSpan={4} style={{ ...S.td, padding: '6px 10px', color: ui.green, fontWeight: 700 }}>Every finance-type item on the site is on the dashboard.</td></tr>}
              {audit.noMoney.length > 0 && (
                <tr>
                  <td style={{ ...S.td, padding: '6px 10px', color: ui.amber, fontWeight: 700 }}>Shown, but the money field is empty or 0 — counts as R 0.00 everywhere</td>
                  <td style={{ ...S.td, ...S.num, padding: '6px 10px' }}>{audit.noMoney.length.toLocaleString()}</td>
                  <td style={{ ...S.td, ...S.num, padding: '6px 10px' }}>—</td>
                  <td style={{ ...S.td, padding: '6px 10px', whiteSpace: 'nowrap' }}><button onClick={() => openInSearch(audit.noMoney, 'no money value')} title="Open these work items in a new Stride tab" style={{ ...S.pill(false), padding: '3px 10px' }}>Open the tickets ↗</button></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ---- admin settings (the Operations custom's AdminSettings box) --------
  const saveConfig = async (patch) => {
    const next = { ...pageCfg, ...patch };
    await api.customsPut(page.slug, { name: page.name, description: page.description || null, kind: page.kind || 'component', config: next, restrictedGroups: page.restrictedGroups || [] });
    setPageCfg(next);
  };
  const countOf = async (zql) => { const r = await api.countIssues(zql); return typeof r === 'object' && r ? (r.count ?? r.total ?? 0) : (r ?? 0); };
  const testZql = async (zql, values) => {
    const base = String(values && values.baseZql != null ? values.baseZql : baseZql).trim();
    return countOf(zql === base ? base : `${base ? `(${base}) AND ` : ''}(${zql})`);
  };
  const listText = (arr) => (Array.isArray(arr) ? arr.join(', ') : '');
  const listOf = (txt) => String(txt || '').split(',').map((x) => x.trim()).filter(Boolean);
  const setOrDrop = (out, k, v) => { if (v && (!Array.isArray(v) || v.length)) out[k] = v; else delete out[k]; };
  const defsState = (list) => list.map((d) => `${d.caption || d.zql}: ${defState(d.id) === 'ready' ? `${keySets[d.id].size.toLocaleString()} items${keySets[d.id].truncated ? ' ⚠ TRUNCATED — not applied' : ''}` : defState(d.id) === 'error' ? `⚠ ${keySets[d.id].error}` : 'resolving…'}`).join(' · ');
  const filtersSettings = (
    <AdminSettings title="Settings — scope, defaults, quick buttons & controls" ui={ui} narrow={narrow}
      fields={[
        { key: 'baseZql', label: 'Background scope (ZQL) — every load is AND-ed with this', value: baseZql, rows: 2, zql: true, placeholder: 'e.g. project NOT IN (TEST) — blank = the whole site', hint: 'Test counts the whole scope (all dates, every work type).' },
        { key: 'datePreset', label: 'Default when the page opens — due date range', type: 'select', value: D.datePreset === 'all' ? 'all' : 'year', options: [{ value: 'year', label: 'This Year' }, { value: 'all', label: 'All dates' }] },
        { key: 'trendFitTo', label: 'Default — trend lines fit to', type: 'select', value: D.trendFitTo === 'this' ? 'this' : 'last', options: [{ value: 'last', label: 'Last Month' }, { value: 'this', label: 'This Month' }] },
        { key: 'seriesOff', label: 'Series unticked on open (the knob’s CUSTOM picture) — comma-separated keys', value: listText(Array.isArray(D.seriesOff) ? D.seriesOff : ['prof']), mono: false, placeholder: 'prof', hint: 'Keys: pexp, texp, deb, pinc, sal, prof, act.' },
        ...[['search', 'Search box'], ['project', 'Project filter'], ['owner', 'Project Owner filter'], ['status', 'Status filter'], ['dateRange', 'Due date range']].map(([k, t]) => ({ key: `show_${k}`, label: `Show — ${t}`, type: 'checkbox', checkboxLabel: 'Shown', value: FV[k] !== false })),
      ]}
      list={{ key: 'filterButtons', label: 'Quick filter buttons', hint: 'caption + ZQL — a button in this card; every widget narrows to it (several on = AND). "On by default" = pressed when the page opens.', items: filterButtons, flags: [{ key: 'on', label: 'On by default', hint: 'Pressed when the page opens' }] }}
      info={[
        `Finance load: ${financeJql} — ${items ? items.length.toLocaleString() : '…'} items.`,
        `Quick buttons: ${filterButtons.length ? defsState(filterButtons) : 'none'}.`,
        'Saved defaults apply from the next open; the scope and the quick buttons apply as soon as they are saved.',
      ]}
      testZql={testZql}
      onSave={({ values, items: list }) => saveConfig({
        baseZql: String(values.baseZql || '').trim(),
        defaults: { ...(pageCfg.defaults || {}), datePreset: values.datePreset === 'all' ? 'all' : 'year', trendFitTo: values.trendFitTo === 'this' ? 'this' : 'last', seriesOff: listOf(values.seriesOff) },
        filters: Object.fromEntries(['search', 'project', 'owner', 'status', 'dateRange'].map((k) => [k, !!values[`show_${k}`]])),
        filterButtons: list,
      })} />
  );
  const columnsSettings = (
    <AdminSettings title="Columns — the money field, the work types per column, the statuses" ui={ui} narrow={narrow}
      fields={[
        { key: 'valueField', label: 'Money field (name or customfield_… id)', value: pageCfg.valueField || '', placeholder: NAME_VALUE, mono: false },
        { key: 'supplierField', label: 'Supplier field', value: pageCfg.supplierField || '', placeholder: NAME_SUPPLIER, mono: false },
        ...KINDS.map((k) => ({ key: k.cfgKey, label: `${k.label} = Σ these work types`, value: listText(pageCfg[k.cfgKey]), placeholder: k.types.join(', '), mono: false })),
        { key: 'projectTypes', label: 'The Project work item (due date · owner · Done)', value: listText(pageCfg.projectTypes), placeholder: DEFAULT_PROJECT_TYPES.join(', '), mono: false },
        { key: 'excludedStatuses', label: 'Statuses never shown', value: listText(pageCfg.excludedStatuses), placeholder: DEFAULT_EXCLUDED_STATUSES.join(', '), mono: false },
        { key: 'paidStatuses', label: 'Statuses that count as PAID', value: listText(pageCfg.paidStatuses), placeholder: DEFAULT_PAID_STATUSES.join(', '), mono: false },
        { key: 'invoicedStatuses', label: 'Statuses that count as INVOICED', value: listText(pageCfg.invoicedStatuses), placeholder: DEFAULT_INVOICED_STATUSES.join(', '), mono: false },
        { key: 'hiddenStatuses', label: 'Statuses deselected in the Status filter on open', value: listText(pageCfg.hiddenStatuses), placeholder: DEFAULT_HIDDEN_STATUSES.join(', '), mono: false },
        { key: 'debtorStatusOrder', label: 'Debtors per Status — column order', value: listText(pageCfg.debtorStatusOrder), placeholder: DEFAULT_STATUS_ORDER.join(', '), mono: false },
      ]}
      info={[
        `Resolved now: money = ${cfg.value || 'NOT FOUND'} · supplier = ${cfg.supplier || 'NOT FOUND'} (field names are matched case- and punctuation-insensitively against /api/fields).`,
        'Blank = the built-in default. Comma-separate lists. Work types and statuses are matched case-insensitively.',
      ]}
      onSave={({ values }) => {
        const patch = {};
        ['valueField', 'supplierField'].forEach((k) => setOrDrop(patch, k, String(values[k] || '').trim()));
        [...KINDS.map((k) => k.cfgKey), 'projectTypes', 'excludedStatuses', 'paidStatuses', 'invoicedStatuses', 'hiddenStatuses', 'debtorStatusOrder'].forEach((k) => setOrDrop(patch, k, listOf(values[k])));
        const next = { ...pageCfg };
        Object.keys(patch).forEach((k) => { next[k] = patch[k]; });
        ['valueField', 'supplierField', ...KINDS.map((k) => k.cfgKey), 'projectTypes', 'excludedStatuses', 'paidStatuses', 'invoicedStatuses', 'hiddenStatuses', 'debtorStatusOrder'].forEach((k) => { if (!(k in patch)) delete next[k]; });
        return saveConfig(next);
      }} />
  );
  // A quick-button pill (admin-defined caption + ZQL).
  const defButton = (d) => {
    const st = defState(d.id); const on = buttonsOn.has(d.id);
    return (
      <button key={d.id} onClick={() => toggleButton(d.id)} disabled={st === 'error'}
        title={`${st === 'error' ? `ZQL failed: ${keySets[d.id].error}` : st === 'loading' ? 'Resolving the ZQL…' : st === 'idle' ? 'Resolves when pressed' : `${keySets[d.id].size.toLocaleString()} items match${keySets[d.id].truncated ? ` — ⚠ TRUNCATED at ${keySets[d.id].truncated.toLocaleString()} keys: narrow the ZQL, items past the cut are NOT hidden` : ''}`} — ${d.zql}\nClick to narrow every widget to this — several buttons on = all of them (AND)`}
        style={{ ...S.pill(on), opacity: st === 'ready' || st === 'idle' ? 1 : 0.55, borderStyle: st === 'error' ? 'dashed' : 'solid', color: st === 'error' ? ui.red : (on ? ui.fg : ui.muted) }}>
        {d.caption || d.zql}{st === 'loading' ? ' …' : st === 'error' ? ' ⚠' : ''}
      </button>
    );
  };

  const filtersCard = (
    <div ref={filtersRef} style={{ position: narrow ? 'static' : 'sticky', top: 0, zIndex: 40, background: ui.card, border: `1px solid ${ui.border}`, borderRadius: ui.radius, boxShadow: ui.shadowSm, padding: '10px 14px', marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <button onClick={() => setFiltersOpen((o) => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted, fontFamily: FONT }}>{chev(filtersOpen ? 'down' : 'right')} Filters</button>
        {filterButtons.map(defButton)}
        {undatedCount > 0 && (dateFrom || dateTo) && <span style={S.tiny} title="Items with no due date cannot sit inside a due-date range; choose “All dates” to include them">{undatedCount.toLocaleString()} undated hidden by the range</span>}
        {!filtersOpen && FV.dateRange && <button onClick={() => setFiltersOpen(true)} title="Date range — open the filters to change it" style={S.pill(datePreset !== 'all')}>{dateLabel}</button>}
        {!filtersOpen && FV.project && <div style={{ flex: '1 1 170px', minWidth: 150, maxWidth: 320 }}>{filterTrigger('projfilter', projLabel, projectSel.size > 0)}</div>}
        {!filtersOpen && FV.owner && <div style={{ flex: '1 1 170px', minWidth: 150, maxWidth: 280 }}>{filterTrigger('ownerfilter', ownerLabel, ownerSel.size > 0)}</div>}
        {!filtersOpen && FV.status && <div style={{ flex: '0 1 200px', minWidth: 150, maxWidth: 240 }}>{filterTrigger('statusfilter', statusLabel, statusOff.size > 0)}</div>}
        {!filtersOpen && search.trim() && <button onClick={() => setSearch('')} title="Clear the search" style={S.pill(true)}>“{search.trim()}” ×</button>}
        <div style={{ flex: 1 }} />
        <span style={S.tiny}>{filtered.length.toLocaleString()} of {financeItems.length.toLocaleString()} finance items{demo ? ' · DEMO DATA' : ''}</span>
        {anyFilter && <button onClick={clearFilters} style={{ ...S.tiny, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT, padding: 0 }}>Clear</button>}
      </div>
      {!filtersOpen && chipsRow}
      {filtersOpen && (
        <div style={{ marginTop: 10 }}>
          {FV.search && <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔎 Search summary, key, project, status or supplier"
            style={{ ...S.input, marginBottom: 10 }} />}
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {FV.project && <div style={{ flex: narrow ? '1 1 100%' : '1 1 260px', minWidth: narrow ? '100%' : 220, maxWidth: 420 }}>
              <label style={S.label}>Project</label>
              {filterTrigger('projfilter', projLabel, projectSel.size > 0)}
            </div>}
            {FV.owner && <div style={{ flex: narrow ? '1 1 100%' : '1 1 240px', minWidth: narrow ? '100%' : 200, maxWidth: 360 }}>
              <label style={S.label}>Project Owner (Project Assigned resource)</label>
              {filterTrigger('ownerfilter', ownerLabel, ownerSel.size > 0)}
            </div>}
            {FV.status && <div style={{ flex: narrow ? '1 1 100%' : '0 1 220px', minWidth: narrow ? '100%' : 180, maxWidth: 280 }}>
              <label style={S.label}>Status</label>
              {filterTrigger('statusfilter', statusLabel, statusOff.size > 0)}
            </div>}
            {FV.dateRange && <div style={{ flex: narrow ? '1 1 100%' : '2 1 340px', minWidth: narrow ? '100%' : 300 }}>
              <label style={S.label}>Due date range</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }} style={{ ...dateInputStyle, width: 'auto' }} />
                <span style={S.tiny}>to</span>
                <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }} style={{ ...dateInputStyle, width: 'auto' }} />
                <button onClick={applyThisYear} style={S.pill(datePreset === 'year')}>This Year</button>
                <button onClick={applyAllDates} style={S.pill(datePreset === 'all')}>All dates</button>
              </div>
            </div>}
          </div>
          {chipsRow}
          {adminMode && filtersSettings}
          {adminMode && auditPanel}
          {adminMode && (
            <div style={{ marginTop: 10, borderTop: `1px solid ${ui.border}`, paddingTop: 8 }}>
              <button onClick={() => setScopeOpen((o) => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted, fontFamily: FONT }}>
                {chev(scopeOpen ? 'down' : 'right')} Data scope — background filtering on this page (admin mode)
              </button>
              {scopeOpen && (
                <div style={{ ...S.tiny, marginTop: 8, lineHeight: 1.7 }}>
                  <div>• Finance load: {code(financeJql)} — {items ? items.length.toLocaleString() : '…'} items, paged {PAGE.toLocaleString()} at a time (cap {MAX_ROWS.toLocaleString()}). Only the finance-bearing work types are fetched, never the whole site. The Project filter works client-side on the item key prefix (ZQL&apos;s {code('project in (…)')} doesn&apos;t match cloned items).</div>
                  <div>• Field wiring — resolved at runtime BY NAME from /api/fields; the page row&apos;s config can override: ZAR Value (Excl VAT) = {code(cfg.value || `“${NAME_VALUE}” NOT FOUND`)} · Supplier List = {code(cfg.supplier || `“${NAME_SUPPLIER}” NOT FOUND`)}.</div>
                  <div>• Columns by WORK TYPE: {cfg.kinds.map((k) => `${k.label} = ${k.names.join(' + ')}`).join(' · ')}. Total Expenses (chart) = Expenses + Salaries &amp; Special Projects. Projected Profit = Projected Income − Projected Expense; Actual Profit = Debtors − (Expenses + Salaries &amp; Special Projects) — the chart columns on the right-hand axis and the two table columns.</div>
                  <div>• Status filter: every status on the finance rows is listed; ticked = shown. {statusOff.size ? <>Currently hiding <strong style={{ color: ui.green }}>{hiddenStatusCount.toLocaleString()}</strong> item{hiddenStatusCount === 1 ? '' : 's'} in {[...statusOff].join(', ')}</> : 'Nothing hidden'}; hidden statuses also drop out of the Debtors per Status columns. The default deselection is the page config&apos;s {code('hiddenStatuses')}.</div>
                  <div>• Project Owner (Project Assigned resource) = the assignee of the project&apos;s “Project” work item{ownerSel.size ? <> — filtering on <strong>{[...ownerSel].join(', ')}</strong> (a project matches when ANY of its owners is selected)</> : ''}. {multiProjectItems.length ? <>{multiProjectItems.length} project{multiProjectItems.length === 1 ? ' carries' : 's carry'} MORE THAN ONE Project work item ({multiProjectItems.map((m) => `${m.key} ×${m.projectItems}`).join(', ')}) — flagged in the Project and Owner menus; every owner of such a project matches the filter.</> : 'No project carries more than one Project work item.'}</div>
                  <div>• Date range (default This Year): an item with NO due date cannot sit inside a range, so it shows only under “All dates” ({undatedCount.toLocaleString()} undated in the current view). Nothing is hidden for being in a Done project.</div>
                  <div>• Debtors per Status: one column per status of the Debtors workflow — lifecycle order first ({cfg.statusOrder.join(' → ')}), any other workflow status after it ({wfStatuses === null ? 'loading…' : (wfStatuses.length ? `workflow: ${wfStatuses.join(', ')}` : 'workflow lookup failed — columns fall back to the statuses found on items')}), hiding “{cfg.excludedStatuses.join('”, “')}”. Invoices NOT PAID = Debtors not in “{cfg.paidStatuses.join('”, “')}”; NOT INVOICED = those also not in “{cfg.invoicedStatuses.join('”, “')}”.</div>
                  <div>• Expenses per Supplier: Procurement Orders by Supplier List — the top {SUPPLIER_LINES} suppliers are charted, every supplier is in the table; a multi-supplier order counts once under the joined name.</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ---- Projects Finance Summary table ------------------------------------
  const sumRows = (rows) => rows.reduce((a, r) => { KINDS.forEach((k) => { a[k.id] += r.v[k.id]; }); return a; }, zeroKinds());
  const exportSummary = () => {
    const aoa = [['#', 'Project', 'Project Due Date', ...KINDS.map((k) => k.label), 'Projected Profit', 'Actual Profit']];
    sortedSummary.forEach((r, i) => { const nm = projNameOf(r.key); aoa.push([i + 1, nm ? `${r.key} (${nm})` : r.key, r.due || '', ...KINDS.map((k) => (r.has[k.id] ? round2(r.v[k.id]) : '')), (r.has.pinc || r.has.pexp) ? round2(profitOf(r.v)) : '', (r.has.deb || r.has.exp || r.has.sal) ? round2(actualOf(r.v)) : '']); });
    aoa.push(['', 'Total (ZAR Value (Excl VAT))', '', ...KINDS.map((k) => round2(totals[k.id])), round2(profitOf(totals)), round2(actualOf(totals))]);
    aoa.push([]); aoa.push(['', INFO_PROJECTED]); aoa.push(['', INFO_ACTUAL]);
    writeSheet(aoa, [{ wch: 4 }, { wch: 56 }, { wch: 14 }, ...KINDS.map(() => ({ wch: 18 })), { wch: 18 }, { wch: 18 }], KINDS.map((_, i) => i + 3).concat([KINDS.length + 3, KINDS.length + 4]), 'Projects Finance Summary', 'projects-finance-summary');
  };
  const summaryTable = (availW) => {
    const id = 'summary';
    const rows = sortedSummary;
    const T = tableFit(1380, availW);
    const shown = isExpanded(id) ? rows : rows.slice(0, SHOW_TOP);
    const restSum = sumRows(rows.slice(SHOW_TOP));
    const totalTd = { ...T.td, ...S.num, fontWeight: 800, background: `${ui.primary}0a`, borderBottom: 'none' };
    const COLS = 5 + KINDS.length;
    const PROJ = { pos: ui.lightBlue, neg: ui.pink }; const ACT = { pos: ui.green, neg: ui.red };
    // the columns wear the colours of the chart series they feed (a light wash)
    const wash = (c, a = '14') => ({ background: `${c}${a}` });
    const kindTd = (k) => ({ ...T.td, ...S.num, color: ui.blue, fontWeight: 600, ...wash(ui[k.colorKey]) });
    const profitTd = (v, has, extra, c = ACT) => <td style={{ ...T.td, ...S.num, fontWeight: 700, color: v < 0 ? c.neg : c.pos, ...wash(has && v < 0 ? c.neg : c.pos), ...(extra || {}) }}>{has ? fmtR(v) : dash(ui)}</td>;
    const row = (r, i) => (
      <tr key={r.key} {...rowClick(r)}>
        <td style={{ ...T.td, color: ui.muted }}>{i + 1}</td>
        {projectCell(T, r)}
        <td style={{ ...T.td, whiteSpace: 'nowrap' }}>{r.due ? fmtDue(r.due) : dash(ui)}</td>
        {KINDS.map((k) => <Fragment key={k.id}>{doorTd(kindTd(k), r.items[k.id], r.has[k.id] ? fmtR(r.v[k.id]) : dash(ui), `${k.label}, ${projNameOf(r.key) || r.key}`)}</Fragment>)}
        {profitTd(profitOf(r.v), r.has.pinc || r.has.pexp, null, PROJ)}
        {profitTd(actualOf(r.v), r.has.deb || r.has.exp || r.has.sal)}
      </tr>
    );
    return (
      <div style={{ ...S.card, padding: 0, overflow: 'clip', marginBottom: 14 }}>
        {titleBar(id, 'Projects Finance Summary', T, exportButton(exportSummary, rows.length > 0))}
        {adminMode && <div style={{ padding: '0 14px 8px' }}>{columnsSettings}</div>}
        {isOpen(id) && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...T.th, width: 44 }}>#</th>
                {sortableTh(T, 'Project', 'project', summarySort, setSortFor(id), false)}
                {sortableTh(T, 'Due date', 'due', summarySort, setSortFor(id), false)}
                {KINDS.map((k) => sortableTh(T, k.label, k.id, summarySort, setSortFor(id), true, { ...wash(ui[k.colorKey], '26'), color: ui.fg }))}
                {sortableTh(T, <>Projected Profit <InfoIcon text={INFO_PROJECTED} title="Projected Profit — how it is calculated" ui={ui} /></>, 'prof', summarySort, setSortFor(id), true, { ...wash(PROJ.pos, '26'), color: ui.fg })}
                {sortableTh(T, <>Actual Profit <InfoIcon text={INFO_ACTUAL} title="Actual Profit — how it is calculated" ui={ui} /></>, 'act', summarySort, setSortFor(id), true, { ...wash(ACT.pos, '26'), color: ui.fg })}
              </tr></thead>
              <tbody>
                {shown.map(row)}
                {showMoreRow(id, T, COLS, rows, <><td style={T.td} />{KINDS.map((k) => <td key={k.id} style={kindTd(k)}>{fmtR(restSum[k.id])}</td>)}{profitTd(profitOf(restSum), true, null, PROJ)}{profitTd(actualOf(restSum), true)}</>)}
                <tr>
                  <td style={{ ...totalTd, textAlign: 'left' }} colSpan={3}><span style={{ color: ui.amber }}>Total (ZAR Value (Excl VAT))</span></td>
                  {KINDS.map((k) => <td key={k.id} style={{ ...totalTd, ...wash(ui[k.colorKey], '26') }}>{fmtR(totals[k.id])}</td>)}
                  {profitTd(profitOf(totals), true, { fontWeight: 800, borderBottom: 'none', ...wash(profitOf(totals) < 0 ? PROJ.neg : PROJ.pos, '26') }, PROJ)}
                  {profitTd(actualOf(totals), true, { fontWeight: 800, borderBottom: 'none', ...wash(actualOf(totals) < 0 ? ACT.neg : ACT.pos, '26') })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ---- a projects × columns money matrix (Debtors per Status, Invoices
  // NOT PAID / NOT INVOICED) ------------------------------------------------
  const exportMatrix = (m, rows, title, stem) => {
    const aoa = [['#', 'Project', ...m.cols.map((c) => c.label), 'Total']];
    rows.forEach((r, i) => { const nm = projNameOf(r.key); aoa.push([i + 1, nm ? `${r.key} (${nm})` : r.key, ...m.cols.map((c) => (r.cols[c.key] != null ? round2(r.cols[c.key]) : '')), round2(r.total)]); });
    aoa.push(['', 'Total (ZAR Value (Excl VAT))', ...m.cols.map((c) => round2(rows.reduce((s, r) => s + (r.cols[c.key] || 0), 0))), round2(rows.reduce((s, r) => s + r.total, 0))]);
    writeSheet(aoa, [{ wch: 4 }, { wch: 56 }, ...m.cols.map(() => ({ wch: 16 })), { wch: 16 }], m.cols.map((_, i) => i + 2).concat([m.cols.length + 2]), title, stem);
  };
  const matrixTable = (id, title, m, emptyText, colColor, availW) => {
    const sort = sortOf(id, { key: 'total', dir: 'desc' });
    const rows = [...m.rows];
    const mul = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sort.key === 'project') return (projNameOf(a.key) || a.key).toLowerCase().localeCompare((projNameOf(b.key) || b.key).toLowerCase()) * mul;
      const va = sort.key === 'total' ? a.total : (a.cols[sort.key] || 0);
      const vb = sort.key === 'total' ? b.total : (b.cols[sort.key] || 0);
      return (va - vb) * mul;
    });
    const { cols } = m;
    const T = tableFit(380 + 120 * (cols.length + 1), availW);
    const shown = isExpanded(id) ? rows : rows.slice(0, SHOW_TOP);
    const rest = rows.slice(SHOW_TOP);
    // Pinned edges: the Project column stays put on the left and the
    // Total on the right while the month columns scroll between them.
    const stickyL = { position: 'sticky', left: 0, zIndex: 1, background: ui.card, boxShadow: `inset -1px 0 0 ${ui.border}` };
    const stickyR = { position: 'sticky', right: 0, zIndex: 1, background: ui.card, boxShadow: `inset 1px 0 0 ${ui.border}` };
    const totalTd = { ...T.td, ...S.num, fontWeight: 800, background: `${ui.primary}0a`, borderBottom: 'none' };
    const row = (r, i) => (
      <tr key={r.key} {...rowClick(r)}>
        <td style={{ ...T.td, color: ui.blue }}>{i + 1}</td>
        {projectCell(T, r, stickyL)}
        {cols.map((c) => <Fragment key={c.key}>{doorTd({ ...T.td, ...S.num, color: (colColor && colColor(c)) || ui.blue, fontWeight: 600 }, r.items[c.key], r.cols[c.key] != null ? fmtR(r.cols[c.key]) : dash(ui), `${c.label}, ${projNameOf(r.key) || r.key}`)}</Fragment>)}
        {doorTd({ ...T.td, ...S.num, fontWeight: 800, ...stickyR }, r.all, fmtR(r.total), projNameOf(r.key) || r.key)}
      </tr>
    );
    return (
      <div style={{ ...S.card, padding: 0, overflow: 'clip', marginBottom: 14 }}>
        {titleBar(id, title, T, exportButton(() => exportMatrix(m, rows, title, id), rows.length > 0))}
        {isOpen(id) && (rows.length === 0 ? (
          <div style={{ ...S.tiny, padding: '4px 14px 16px', textAlign: 'center' }}>{emptyText}</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...T.th, width: 44 }}>#</th>
                {sortableTh(T, 'Project', 'project', sort, setSortFor(id), false, stickyL)}
                {cols.map((c) => sortableTh(T, c.label, c.key, sort, setSortFor(id), true))}
                {sortableTh(T, 'Total', 'total', sort, setSortFor(id), true, stickyR)}
              </tr></thead>
              <tbody>
                {shown.map(row)}
                {showMoreRow(id, T, cols.length + 3, rows, <>{cols.map((c) => <td key={c.key} style={{ ...T.td, ...S.num, color: ui.blue, fontWeight: 600 }}>{fmtR(rest.reduce((s, r) => s + (r.cols[c.key] || 0), 0))}</td>)}<td style={{ ...T.td, ...S.num, fontWeight: 800, ...stickyR }}>{fmtR(rest.reduce((s, r) => s + r.total, 0))}</td></>)}
                <tr>
                  <td style={{ ...totalTd, textAlign: 'left' }} colSpan={2}><span style={{ color: ui.amber }}>Total (ZAR Value (Excl VAT))</span></td>
                  {cols.map((c) => <td key={c.key} style={totalTd}>{fmtR(rows.reduce((s, r) => s + (r.cols[c.key] || 0), 0))}</td>)}
                  <td style={{ ...totalTd, ...stickyR, background: ui.card }}>{fmtR(rows.reduce((s, r) => s + r.total, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}
      </div>
    );
  };

  // ---- Expenses per Supplier: the chart + the ranked table ---------------
  const exportSuppliers = () => {
    const aoa = [['#', 'Supplier', 'Orders', 'ZAR Value (Excl VAT)']];
    sortedSuppliers.forEach((r, i) => aoa.push([i + 1, r.key, r.count, round2(r.total)]));
    aoa.push(['', 'Total (ZAR Value (Excl VAT))', sortedSuppliers.reduce((s, r) => s + r.count, 0), round2(totals.exp)]);
    writeSheet(aoa, [{ wch: 4 }, { wch: 44 }, { wch: 10 }, { wch: 20 }], [3], 'Expenses per Supplier', 'expenses-per-supplier');
  };
  const supplierTable = () => {
    const id = 'suppliers';
    const rows = sortedSuppliers;
    const T = tableFit(720);
    const shown = isExpanded(id) ? rows : rows.slice(0, 13);
    const totalTd = { ...T.td, ...S.num, fontWeight: 800, background: `${ui.primary}0a`, borderBottom: 'none' };
    return (
      <div style={{ ...S.card, padding: 0, overflow: 'clip', marginBottom: 14 }}>
        {titleBar(id, 'Expenses per Supplier', T, exportButton(exportSuppliers, rows.length > 0))}
        {isOpen(id) && (rows.length === 0 ? (
          <div style={{ ...S.tiny, padding: '4px 14px 16px', textAlign: 'center' }}>No Procurement Orders in the current filters.</div>
        ) : (<>
          {/* the chart is capped at 1100px so it never balloons across a wide page */}
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 14px' }}>
            {supplierSel.size > 0 ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 2px' }}>
                <span style={{ ...S.tiny, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: 10.5 }}>Charting</span>
                {[...supplierSel].map((name) => chip(name, () => toggleSupplier(name), (supplierAgg.find((r) => r.key === name) || {}).color || ui.muted))}
                <button onClick={() => setSupplierSel(new Set())} style={{ ...S.pill(false), padding: '3px 10px', fontSize: 12 }}>Clear — back to the top {SUPPLIER_LINES}</button>
              </div>
            ) : (
              <div style={{ ...S.tiny, margin: '4px 0 2px' }}>The top {SUPPLIER_LINES} suppliers — click suppliers in the list below to chart those instead (any number of them).</div>
            )}
            <LineChart title="Expenses per Supplier" points={supplierPoints} seriesDefs={supplierSeries} cumulative={false} ui={ui} narrow legendLimit={6} emptyText={supplierSel.size ? 'The picked suppliers have no orders in the current filters.' : 'No Procurement Orders in the current filters.'} onPick={pickFromSupplierChart} />
          </div>
          {/* the ranked supplier list — collapsible on its own */}
          <button onClick={() => toggleOpen('supplier-list')} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%', background: 'none', border: 'none', borderTop: `1px solid ${ui.border}`, cursor: 'pointer', padding: '10px 14px', fontFamily: FONT, color: ui.fg }}>
            <span style={{ fontSize: 12, transform: isOpen('supplier-list') ? 'rotate(90deg)' : 'none', transition: 'transform .12s', display: 'inline-block' }}>▸</span>
            <span style={{ fontSize: 13.5, fontWeight: 800 }}>Supplier list</span>
            <span style={S.tiny}>· {rows.length.toLocaleString()} supplier{rows.length === 1 ? '' : 's'} · {fmtR(totals.exp)}</span>
          </button>
          {isOpen('supplier-list') && <div style={{ overflowX: 'auto', maxWidth: 820, margin: '0 auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...T.th, width: 44 }}>#</th>
                {sortableTh(T, 'Supplier List', 'supplier', supplierSort, setSortFor(id), false)}
                {sortableTh(T, 'Orders', 'count', supplierSort, setSortFor(id), true)}
                {sortableTh(T, 'ZAR Value (Excl VAT)', 'total', supplierSort, setSortFor(id), true)}
              </tr></thead>
              <tbody>
                {shown.map((r, i) => {
                  const sel = supplierSel.has(r.key);
                  const bg = sel ? `${r.color || ui.blue}1f` : 'transparent';
                  return (
                    <tr key={r.key} onClick={() => toggleSupplier(r.key)} title={sel ? 'Charted — click to take this supplier off the chart' : 'Click to chart this supplier (add as many as you like)'} style={{ cursor: 'pointer', background: bg }}
                      onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = ui.surface; }} onMouseLeave={(e) => { e.currentTarget.style.background = bg; }}>
                      <td style={{ ...T.td, color: ui.muted, whiteSpace: 'nowrap' }}>
                        <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 12, background: r.color || 'transparent', border: r.color ? 'none' : `1px dashed ${ui.border}`, marginRight: 8, verticalAlign: -1 }} />{i + 1}
                      </td>
                      <td style={{ ...T.td, whiteSpace: 'normal', wordBreak: 'break-word', color: ui.blue, fontWeight: sel ? 700 : 400 }}>{sel ? '✓ ' : ''}{r.key}</td>
                      {doorTd({ ...T.td, ...S.num }, r.items, <>{r.count.toLocaleString()} <span style={{ fontSize: 11, fontWeight: 800, color: ui.blue }}>↗</span></>, `${r.key} orders`)}
                      {doorTd({ ...T.td, ...S.num, color: ui.blue, fontWeight: 600 }, r.items, fmtR(r.total), `${r.key} orders`)}
                    </tr>
                  );
                })}
                {rows.length > 13 && (
                  <tr onClick={() => setExpanded(id, !isExpanded(id))} style={{ cursor: 'pointer' }} onMouseEnter={(e) => { e.currentTarget.style.background = ui.surface; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                    <td colSpan={4} style={{ ...T.td, color: ui.muted, fontWeight: 600 }}><span style={{ fontSize: 11, marginRight: 6 }}>{isExpanded(id) ? '▾' : '▸'}</span>{isExpanded(id) ? 'Show fewer' : `Show more… (${rows.length - 13})`}</td>
                  </tr>
                )}
                <tr>
                  <td style={{ ...totalTd, textAlign: 'left' }} colSpan={2}><span style={{ color: ui.amber }}>Total (ZAR Value (Excl VAT))</span></td>
                  <td style={totalTd}>{rows.reduce((s, r) => s + r.count, 0).toLocaleString()}</td>
                  <td style={totalTd}>{fmtR(totals.exp)}</td>
                </tr>
              </tbody>
            </table>
          </div>}
        </>))}
      </div>
    );
  };

  // ---- Summary of Invoiceable Work: Client Name × Assigned Resource -----
  const exportHours = () => {
    const m = hoursMatrix;
    const aoa = [['#', 'Client Name', ...m.cols.map((c) => c.label), 'Total (Actual Time (Hours))']];
    m.rows.forEach((r, i) => aoa.push([i + 1, r.key, ...m.cols.map((c) => (r.cols[c.key] != null ? round1(r.cols[c.key]) : '')), round1(r.total)]));
    aoa.push(['', 'Total (Actual Time (Hours))', ...m.cols.map((c) => round1(m.rows.reduce((s, r) => s + (r.cols[c.key] || 0), 0))), round1(m.rows.reduce((s, r) => s + r.total, 0))]);
    writeSheet(aoa, [{ wch: 4 }, { wch: 40 }, ...m.cols.map(() => ({ wch: 16 })), { wch: 18 }], m.cols.map((_, i) => i + 2).concat([m.cols.length + 2]), 'Summary Of Invoiceable Work', 'summary-of-invoiceable-work', '#,##0.0');
  };
  const hoursTable = () => {
    const id = 'hours';
    const m = hoursMatrix;
    const sort = sortOf(id, { key: 'total', dir: 'desc' });
    const rows = [...m.rows];
    const mul = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      if (sort.key === 'client') return a.key.toLowerCase().localeCompare(b.key.toLowerCase()) * mul;
      const va = sort.key === 'total' ? a.total : (a.cols[sort.key] || 0);
      const vb = sort.key === 'total' ? b.total : (b.cols[sort.key] || 0);
      return (va - vb) * mul;
    });
    const { cols } = m;
    const T = tableFit(300 + 110 * (cols.length + 1));
    const totalTd = { ...T.td, ...S.num, fontWeight: 800, background: `${ui.primary}0a`, borderBottom: 'none' };
    const stickyL = { position: 'sticky', left: 0, background: ui.card, zIndex: 1 };
    return (
      <div style={{ ...S.card, padding: 0, overflow: 'clip', marginBottom: 14 }}>
        {titleBar(id, 'Summary of Invoiceable Work', T, exportButton(exportHours, rows.length > 0))}
        {isOpen(id) && (hoursItems === null ? (
          <div style={{ ...S.tiny, padding: '4px 14px 16px', textAlign: 'center' }}>Loading the invoiceable work…</div>
        ) : hoursError ? (
          <div style={{ ...S.tiny, padding: '4px 14px 16px', textAlign: 'center', color: ui.red }}>{hoursError}</div>
        ) : rows.length === 0 ? (
          <div style={{ ...S.tiny, padding: '4px 14px 16px', textAlign: 'center' }}>{cfg.hours ? 'No work with Actual Time (Hours) in the current project / date scope.' : `No site field named “${NAME_HOURS}” was found — set "hoursField" in this page's config.`}</div>
        ) : (<>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={{ ...T.th, width: 44, ...stickyL }}>#</th>
                {sortableTh(T, 'Client Name', 'client', sort, setSortFor(id), false, { ...stickyL, left: 44 + 24 * T.f, minWidth: 160 })}
                {cols.map((c) => sortableTh(T, c.label, c.key, sort, setSortFor(id), true))}
                {sortableTh(T, 'Total', 'total', sort, setSortFor(id), true)}
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.key}>
                    <td style={{ ...T.td, color: ui.muted, ...stickyL }}>{i + 1}</td>
                    <td style={{ ...T.td, whiteSpace: 'nowrap', ...stickyL, left: 44 + 24 * T.f }}>{r.key}</td>
                    {cols.map((c) => <Fragment key={c.key}>{doorTd({ ...T.td, ...S.num, color: ui.blue, fontWeight: 600 }, r.items[c.key], r.cols[c.key] != null ? fmtH(r.cols[c.key]) : dash(ui), `${c.label}, ${r.key}`)}</Fragment>)}
                    {doorTd({ ...T.td, ...S.num, fontWeight: 800 }, r.all, fmtH(r.total), r.key)}
                  </tr>
                ))}
                <tr>
                  <td style={{ ...totalTd, textAlign: 'left', ...stickyL, background: ui.card }} colSpan={2}><span style={{ color: ui.amber }}>Total (Actual Time (Hours))</span></td>
                  {cols.map((c) => <td key={c.key} style={totalTd}>{fmtH(rows.reduce((s, r) => s + (r.cols[c.key] || 0), 0))}</td>)}
                  <td style={totalTd}>{fmtH(rows.reduce((s, r) => s + r.total, 0))}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ ...S.tiny, padding: '8px 14px 12px' }}>This data shows a summary of <strong style={{ color: ui.fg }}>actual hours per client</strong> — {hoursScoped.length.toLocaleString()} work item{hoursScoped.length === 1 ? '' : 's'} with Actual Time (Hours) in the current project / date scope.</div>
        </>))}
      </div>
    );
  };

  // ---- the shared series selection (both charts) --------------------------
  const seriesBar = () => {
    const all = [...CHART_SERIES.map((d) => ({ ...d, kind: 'line' })), ...PROFIT_BARS.map((d) => ({ ...d, kind: 'bar' }))];
    const visible = all.filter((d) => !seriesOff.has(d.key)).map((d) => d.key);
    const same = (keys) => visible.length === keys.length && keys.every((k) => visible.includes(k));
    // The knob: left = only the projections, right = only the actuals,
    // top (CUSTOM) = neither — the default picture (every line + Actual
    // Profit) or whatever was ticked by hand.
    const mode = same(PROJECTION_KEYS) ? 'proj' : same(ACTUAL_KEYS) ? 'act' : 'none';
    const isDefault = seriesOff.size === seriesDefault.size && [...seriesOff].every((k) => seriesDefault.has(k));
    const showOnly = (keys) => setSeriesOff(new Set(all.map((d) => d.key).filter((k) => !keys.includes(k))));
    const onSwitch = (v) => { if (v === 'proj') showOnly(PROJECTION_KEYS); else if (v === 'act') showOnly(ACTUAL_KEYS); else setSeriesOff(new Set(seriesDefault)); };
    const swatch = (d) => (d.kind === 'bar' ? (
      <span style={{ display: 'inline-flex', gap: 2 }}>
        <span style={{ width: 6, height: 13, borderRadius: 2, background: d.posColor, display: 'inline-block' }} />
        <span style={{ width: 6, height: 13, borderRadius: 2, background: d.negColor, display: 'inline-block' }} />
      </span>
    ) : <span style={{ width: 13, height: 13, borderRadius: 13, background: d.color, display: 'inline-block' }} />);
    return (
      <div style={{ ...S.card, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ ...S.tiny, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Series — both charts</span>
        <KnobToggle value={mode} onChange={onSwitch} ui={ui}
          leftTitle="Only the projections: Projected Expense · Projected Income · Projected Profit"
          topTitle="Custom: every line plus Actual Profit by default — or whatever you tick below"
          rightTitle="Only the actuals: Debtors · Total Expenses INCL Salaries & Overtime · Salaries & Overtime · Actual Profit" />
        <span style={{ width: 1, alignSelf: 'stretch', background: ui.border }} />
        {all.map((d) => { const on = !seriesOff.has(d.key); const tOn = trendKeys.has(d.key); return (
          <span key={d.key} style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <label title={on ? 'Untick to hide this series on both charts' : 'Tick to show this series on both charts'}
              style={{ ...S.tiny, display: 'inline-flex', alignItems: 'center', gap: 7, cursor: 'pointer', padding: '3px 8px', borderRadius: 8, border: `1px solid ${on ? ui.border : 'transparent'}`, background: on ? ui.card : 'transparent', opacity: on ? 1 : 0.55 }}>
              <input type="checkbox" checked={on} onChange={() => toggleSeries(d.key)} style={{ width: 15, height: 15, margin: 0, accentColor: d.color || d.posColor || ui.blue, cursor: 'pointer' }} />
              {swatch(d)}
              <span style={{ fontWeight: 700, color: ui.fg, textDecoration: on ? 'none' : 'line-through' }}>{d.label}</span>
              {d.info && <InfoIcon text={d.info} title={`${d.label} — how it is calculated`} ui={ui} size={15} />}
            </label>
            {/* the joystick: this series' trend line (max two on at a time) */}
            <TrendToggle on={tOn} blocked={!tOn && trendKeys.size >= 2} onToggle={() => toggleTrend(d.key)} label={d.label} />
          </span>
        ); })}
        {mode === 'none' && !isDefault && <button onClick={() => setSeriesOff(new Set(seriesDefault))} style={{ ...S.tiny, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontFamily: FONT, padding: 0 }}>custom selection — reset</button>}
        {trendControls()}
      </div>
    );
  };

  // ---- the ONE trend control row (Fit to · outliers · optimize) ----------
  const trendControls = () => {
    const note = <span style={{ ...S.tiny, fontWeight: 700, color: ui.amber }}>Only two TRENDLINE toggles can be on at the same time.</span>;
    if (!trendKeys.size) {
      return <span style={{ ...S.tiny, flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>Trend lines <InfoIcon text={INFO_TREND} title="Trend lines — how they are fitted" ui={ui} size={15} link={CTT_LINK} /> — push a joystick up under a series to draw its trend line on both charts. {note}</span>;
    }
    const active = [...trendKeys];
    const optimal = trendOptimal;
    const auto = trendPctManual == null;
    const atOptimum = optimal != null && trendPct === optimal;
    const relText = active.map((k) => { const st = trendStats[k]; const d = [...CHART_SERIES, ...PROFIT_BARS].find((x) => x.key === k); return `${d?.label || k} ${st && typeof st.reliability === 'number' ? `${(st.reliability * 100).toFixed(1)}%` : 'n/a'}`; }).join(' · ');
    const small = { fontSize: 12, fontWeight: 600, fontFamily: FONT };
    const stepBtn = (label, next, enabled) => (
      <button onClick={() => enabled && setTrendPct(next)} disabled={!enabled} style={{ width: 22, height: 22, borderRadius: 6, border: `1px solid ${ui.border}`, background: ui.card, color: enabled ? ui.fg : ui.faint, cursor: enabled ? 'pointer' : 'default', fontSize: 14, lineHeight: 1, padding: 0, fontFamily: FONT }}>{label}</button>
    );
    return (
      <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: `1px solid ${ui.border}`, paddingTop: 8, marginTop: 2 }}>
        <span style={{ ...S.tiny, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', display: 'inline-flex', alignItems: 'center', gap: 6 }}>Trend lines <InfoIcon text={INFO_TREND} title="Trend lines — how they are fitted" ui={ui} size={15} link={CTT_LINK} /></span>
        <span style={{ ...S.tiny }}>· Fit to</span>
        <select value={trendFitTo} onChange={(e) => setTrendFitTo(e.target.value)} title="The period the trend is fitted up to — the projection starts from there. Last Month avoids the half-finished current period dragging the slope."
          style={{ ...small, border: `1px solid ${ui.border}`, background: ui.card, color: ui.fg, borderRadius: 7, padding: '3px 6px', cursor: 'pointer' }}>
          <option value="last">Last Month</option>
          <option value="this">This Month</option>
        </select>
        <span style={S.tiny}>· Remove Outliers (%)</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
          {stepBtn('−', Math.max(0, trendPct - 5), trendPct > 0)}
          <span style={{ ...small, minWidth: 34, textAlign: 'center', fontWeight: 700, color: ui.fg, fontVariantNumeric: 'tabular-nums' }}>{trendPct}%</span>
          {stepBtn('+', Math.min(50, trendPct + 5), trendPct < 50)}
        </span>
        {/* the green box: AUTO-optimised by default — the outlier % follows the
            optimum; stepping it by hand leaves auto, clicking the box returns */}
        <button onClick={atOptimum ? undefined : () => setTrendPctManual(null)}
          title={optimal == null ? 'Needs at least five fitted periods — showing the reliability at the current %' : atOptimum ? `${auto ? 'Auto-optimised: ' : ''}${optimal}% is the exclusion where the 95% band is narrowest on average` : `Return to the optimum — ${optimal}% is the exclusion where the 95% band is narrowest on average (most certainty gained before removing data starts costing more than it buys)`}
          style={{ ...small, border: `1px solid ${atOptimum ? ui.green : ui.border}`, background: atOptimum ? `${ui.green}14` : ui.card, color: atOptimum ? ui.green : ui.fg, borderRadius: 7, padding: '4px 10px', cursor: atOptimum ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700 }}>{atOptimum ? `✓ Optimal ${optimal}%` : optimal == null ? `${trendPct}%` : `Optimize (${optimal}%)`}</span>
          <span style={{ opacity: 0.7 }}>·</span>
          <span style={{ fontWeight: 600 }}>reliability</span>
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{relText}</strong>
        </button>
        {note}
      </div>
    );
  };

  const missingFields = fieldsReady && [!cfg.value && NAME_VALUE, !cfg.supplier && NAME_SUPPLIER].filter(Boolean);
  const statusColor = (c) => (cfg.paidStatuses.includes(c.key) ? ui.green : ui.blue);

  return (
    <div style={S.scroll}>
      <div ref={wrapRef} style={S.wrap}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <h1 style={S.h1}>{page?.name || 'High Level Finance'}</h1>
        </div>

        {filtersCard}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '0 0 6px' }}>
          <button onClick={() => setTilesOpen((o) => !o)} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: ui.muted, fontFamily: FONT }}>{chev(tilesOpen ? 'down' : 'right')} Totals</button>
          {!tilesOpen && cfg.kinds.map((k) => (
            <button key={k.id} onClick={() => setViewKind(viewKind === k.id ? '' : k.id)} title={viewKind === k.id ? 'Filtering on this tile — click to clear' : `Show only ${k.label.toLowerCase()}`}
              style={{ ...S.pill(viewKind === k.id), display: 'inline-flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 9, height: 9, borderRadius: 9, background: ui[k.colorKey], display: 'inline-block' }} />{k.label}<strong style={{ color: ui.fg, fontVariantNumeric: 'tabular-nums' }}>{fmtR(totals[k.id]) || 'R 0.00'}</strong>
            </button>
          ))}
        </div>
        {tilesOpen && <div style={{ display: narrow ? 'flex' : 'grid', gridTemplateColumns: `repeat(${cfg.kinds.length}, minmax(0, 1fr))`, gap: narrow ? 8 : 12, flexWrap: 'wrap', marginBottom: 14 }}>
          {cfg.kinds.map(statCard)}
        </div>}

        {error && <div style={{ ...S.card, borderColor: `${ui.red}55`, background: `${ui.red}0d`, marginBottom: 16 }}>{error}</div>}
        {missingFields && missingFields.length > 0 && (
          <div style={{ ...S.card, borderColor: `${ui.amber}66`, background: `${ui.amber}0d`, marginBottom: 16, fontSize: 13 }}>
            <strong>Field wiring:</strong> no site field named {missingFields.map((n) => `“${n}”`).join(', ')} was found — the widgets that depend on it stay empty.
            An admin can set the exact field id(s) in this page&apos;s config (Settings → Content → Client Customs → high-level-finance): {code('{ "valueField": "customfield_…", "supplierField": "…", "clientField": "…", "hoursField": "…" }')}
          </div>
        )}

        {items === null ? (
          <LoadingCard ui={ui} S={S} title="Loading the finance data" note="one paged query over the finance work types — everything after that filters without another load" />
        ) : (<>
          <div style={chartPairStyle}>
            <LineChart title="Expenses & Incomes" points={chartPoints} seriesDefs={CHART_SERIES} bars={PROFIT_BARS} cumulative={false} ui={ui} narrow={narrow || chartSide} height={chartH} hidden={seriesOff} trend={trendOpts} onTrendStats={onTrendStats} onPick={pickFromChart} />
            <LineChart title="Expenses & Incomes — Cumulative" points={chartPoints} seriesDefs={CHART_SERIES} bars={PROFIT_BARS} cumulative ui={ui} narrow={narrow || chartSide} height={chartH} hidden={seriesOff} trend={trendOpts} onPick={pickFromChart} />
          </div>
          {seriesBar()}
          <div style={pairStyle}>
            {summaryTable(halfW)}
            {matrixTable('by-status', 'Debtors per Status', byStatus, 'No Debtors items in the current filters.', statusColor, halfW)}
          </div>
          {matrixTable('not-paid', 'Invoices NOT PAID', notPaid, 'No unpaid Debtors items in the current filters.')}
          {matrixTable('not-invoiced', 'Invoices NOT INVOICED', notInvoiced, 'No un-invoiced Debtors items in the current filters.')}
          {supplierTable()}
        </>)}
      </div>
      {renderMenu()}
    </div>
  );
}
